import { useState, useEffect, useRef, useCallback } from "react";
import "../../CSS/ConsignmentBooking.css";
import useDraggableScroll from "../../hooks/useDraggableScroll";
import api from "../../api";
// NOTE: adjust this path to wherever Barcodelabel.jsx actually lives in your
// project — this assumes it sits in the same folder as ConsignmentBooking.jsx
//import Barcodelabel from "../../src/Components/Reports/Barcodelabel";
import Barcodelabel from "../../Components/Reports/Barcodelabel";

// ── External: JsBarcode is loaded via CDN script tag in index.html:
// <script src="https://cdnjs.cloudflare.com/ajax/libs/JsBarcode/3.11.5/JsBarcode.all.min.js"></script>

/* ══════════════════════════════════════════════════
   STATIC FALLBACK DATA
══════════════════════════════════════════════════ */
const PLACE_NAMES = {
  MUM: "Mumbai",    DEL: "Delhi",      BLR: "Bengaluru",
  CHE: "Chennai",   HYD: "Hyderabad",  PUN: "Pune",
  AHM: "Ahmedabad", KOL: "Kolkata",    JKP: "Jaipur",
  LKN: "Lucknow",   NAG: "Nagpur",     SRT: "Surat",
};
const PINCODES = {
  MUM: "400001", DEL: "110001", BLR: "560001",
  CHE: "600001", HYD: "500001", PUN: "411001",
  AHM: "380001", KOL: "700001", JKP: "302001",
  LKN: "226001", NAG: "440001", SRT: "395003",
};
const PARTIES = {
  TML: { name: "Tata Motors Ltd.",      gst: "27AAACT2727Q1ZW", rate: 18, basis: "perkg" },
  MSI: { name: "Maruti Suzuki India",   gst: "08AAACM4685R1Z5", rate: 22, basis: "perkg" },
  BAL: { name: "Bajaj Auto Ltd.",       gst: "27AAACB0534F1Z5", rate: 15, basis: "perkg" },
  BSH: { name: "Bosch India",           gst: "29AAACB4849M1ZX", rate: 25, basis: "perpkg" },
  MNL: { name: "Motherson Neeraj Ltd.", gst: "07AABCM1787R1ZA", rate: 20, basis: "perkg" },
  SVP: { name: "Sundaram Valeo Pvt.",   gst: "33AAACS1049Q1ZX", rate: 17, basis: "perkg" },
};
const PARTY_JOBS = {
  TML: ["Truck Division", "Car Division", "EV Parts", "After-Market", "Exports", "R&D Spares"],
  MSI: ["Passenger Cars", "Commercial", "Export Parts", "Dealer Supply", "OEM Parts"],
  BAL: ["2W Division", "3W Division", "EV Division", "International", "Racing"],
  BSH: ["Automotive", "Industrial", "Aftermarket", "Export"],
  MNL: ["Wire Harness", "Plastic Parts", "Metal Parts", "EV Components"],
  SVP: ["Thermal Systems", "Friction Parts", "Transmission", "Electrical"],
};
const PKG_TYPES = ["Box / Carton", "Wooden Crate", "Pallet", "Drum", "Bag / Sack", "Bundle", "Loose / Naked"];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/* ══════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════ */
function fmtDate(d) {
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
function fmt(n) {
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
function calcExpDeliveryDate(priority, bookingDate) {
  const days = { standard: 3, express: 1, urgent: 0 };
  const base = bookingDate ? new Date(bookingDate) : new Date();
  const d = new Date(base);
  d.setDate(d.getDate() + (days[priority] || 3));
  return d.toISOString().split('T')[0];
}
function formatDateDisplay(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

// Scrolls the page to the top. Handles the case where this component is
// embedded inside a parent app shell that has its OWN scrollable container
// (so `window`/`document` never actually scrolls) by walking up from a
// given element to find every scrollable ancestor and scrolling each one.
function scrollPageToTop(fromEl) {
  try {
    // 1) Always try window/document — covers the simple case.
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTo?.({ top: 0, behavior: "smooth" });
    document.body.scrollTo?.({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // 2) Walk up from the given element (or .cb-root) and scroll any
    //    ancestor that is actually scrollable — this is the case when a
    //    parent layout/shell owns the scrollbar instead of the window.
    let el = fromEl || document.querySelector(".cb-root");
    while (el && el !== document.body && el !== document.documentElement) {
      const style = window.getComputedStyle(el);
      const canScroll = /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
      if (canScroll) {
        el.scrollTo ? el.scrollTo({ top: 0, behavior: "smooth" }) : (el.scrollTop = 0);
      }
      el = el.parentElement;
    }
  } catch (err) {
    // ignore — scrolling is a nice-to-have, never let it break the booking flow
  }
}


// /* ══════════════════════════════════════════════════
//    AUTOCOMPLETE COMPONENT (old design classes)
// ══════════════════════════════════════════════════ */
// function AutoComplete({ options, value, onChange, placeholder = "Search…" }) {
//   const [query, setQuery] = useState("");
//   const [open, setOpen] = useState(false);
//   const [focused, setFocused] = useState(-1);
//   const wrapRef = useRef(null);

//   const selected = options.find((o) => o.value === value);

//   useEffect(() => {
//     if (selected) setQuery(selected.label);
//     else setQuery("");
//   }, [value, selected]);

//   const filtered = options.filter(
//     (o) => o.value !== undefined && o.label.toLowerCase().includes((query || "").toLowerCase())
//   );

//   function hl(text, q) {
//     if (!q) return text;
//     const i = text.toLowerCase().indexOf(q.toLowerCase());
//     if (i < 0) return text;
//     return (<>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>);
//   }

//   function choose(opt) {
//     onChange(opt.value);
//     setQuery(opt.label);
//     setOpen(false);
//     setFocused(-1);
//   }

//   function clear() {
//     onChange("");
//     setQuery("");
//     setOpen(true);
//     setFocused(-1);
//   }

//   useEffect(() => {
//     function handle(e) {
//       if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
//     }
//     document.addEventListener("mousedown", handle);
//     return () => document.removeEventListener("mousedown", handle);
//   }, []);

//   function handleKeyDown(e) {
//     if (!open) { if (e.key === "ArrowDown" || e.key === "Enter") setOpen(true); return; }
//     if (e.key === "ArrowDown") { e.preventDefault(); setFocused((f) => Math.min(f + 1, filtered.length - 1)); }
//     else if (e.key === "ArrowUp") { e.preventDefault(); setFocused((f) => Math.max(f - 1, 0)); }
//     else if (e.key === "Enter") { e.preventDefault(); if (focused >= 0) choose(filtered[focused]); else if (filtered.length === 1) choose(filtered[0]); }
//     else if (e.key === "Escape") setOpen(false);
//   }

//   return (
//     <div className="cb-ac" ref={wrapRef}>
//       <div className={`cb-ac-box${open ? " open" : ""}`}>
//         <input
//           className="cb-ac-in"
//           type="text"
//           autoComplete="off"
//           placeholder={placeholder}
//           value={query}
//           onFocus={() => setOpen(true)}
//           onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
//           onKeyDown={handleKeyDown}
//         />
//         {value && (
//           <span className="cb-ac-x on" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
//         )}
//         <span className="cb-ac-chev" onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}>▾</span>
//       </div>
//       {open && (
//         <div className="cb-ac-drop open">
//           {filtered.length === 0 ? (
//             <div className="cb-ac-empty">No matches found</div>
//           ) : (
//             filtered.map((opt, i) => (
//               <div
//                 key={opt.value}
//                 className={`cb-ac-opt${opt.value === value ? " sel" : ""}${i === focused ? " foc" : ""}`}
//                 onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
//               >
//                 {hl(opt.label, query)}
//               </div>
//             ))
//           )}
//         </div>
//       )}
//     </div>
//   );
// }

// function handleEnterKey(e) {
//   if (e.key !== "Enter") return;
//   e.preventDefault();

//   // Get all focusable inputs/selects in the entire form (in DOM order)
//   const focusable = Array.from(
//     document.querySelectorAll(
//       'input:not([disabled]):not([readonly]), select:not([disabled]), textarea:not([disabled])'
//     )
//   );

//   const current = e.target;
//   const idx = focusable.indexOf(current);
//   if (idx !== -1 && idx < focusable.length - 1) {
//     focusable[idx + 1].focus();
//   }
// }



function AutoComplete({ options, value, onChange, placeholder = "Search…", onEnter, inputId }) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);   // renamed from 'open' to avoid ESLint conflict
  const [focused, setFocused] = useState(-1);
  const [cursorMoved, setCursorMoved] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (selected) setQuery(selected.label);
    else setQuery("");
  }, [value, selected]);

  //without handle null
  // const filtered = options.filter(
  //   (o) => o.value !== undefined && o.label.toLowerCase().includes((query || "").toLowerCase())
  // );
//with handle null/undefined
  const filtered = options.filter(
  (o) => o.value !== undefined && o.label != null && o.label.toLowerCase().includes((query || "").toLowerCase())
);

  // Keep the keyboard-highlighted option scrolled into view inside the
  // dropdown — without this, ArrowDown/ArrowUp moves the highlight past
  // row ~6-7 (the dropdown's max-height) but it's invisible until the user
  // manually scrolls, which looks like navigation has stopped working.
  // Wrapped in try/catch: some embedding contexts restrict scrollIntoView,
  // and an uncaught error here would otherwise stop ArrowDown/ArrowUp from
  // working at all from that point on.
  useEffect(() => {
    if (focused >= 0 && optionRefs.current[focused]) {
      try {
        optionRefs.current[focused].scrollIntoView({ block: "nearest" });
      } catch (err) {
        // ignore — scrolling is a nice-to-have, never block keyboard nav
      }
    }
  }, [focused]);

  function hl(text, q) {
    if (!q) return text;
    const i = text.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return text;
    return (<>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>);
  }

  // function choose(opt) {
  //   onChange(opt.value);
  //   setQuery(opt.label);
  //   setIsOpen(false);
  //   setFocused(-1);
  //   setCursorMoved(false);
  //   setTimeout(() => inputRef.current?.blur(), 10);
  // }
function choose(opt) {
  onChange(opt.value);
  setQuery(opt.label);
  setIsOpen(false);
  setFocused(-1);
  setCursorMoved(false);
  // Don't blur — let fireEnter focus next field instead
}

  function clear() {
    onChange("");
    setQuery("");
    setIsOpen(true);
    setFocused(-1);
  }

  useEffect(() => {
    function handle(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  // function fireEnter() {
  //   if (onEnter) onEnter({ target: inputRef.current, preventDefault: () => {} });
  // }

function fireEnter() {
  // If a custom onEnter handler is provided (e.g. Priority → Next button), use it
  if (onEnter) { onEnter(); return; }
  const focusable = Array.from(
    document.querySelectorAll(
      'input:not([disabled]):not([readonly]):not([type="hidden"]):not([type="checkbox"]), select:not([disabled]), textarea:not([disabled]):not([readonly]), button:not([disabled])'
    )
  ).filter(el => el.offsetParent !== null);
  const idx = focusable.indexOf(inputRef.current);
  if (idx !== -1 && idx < focusable.length - 1) {
    focusable[idx + 1].focus();
  }
}
  function handleKeyDown(e) {
    // Tab: if a custom onEnter override exists (e.g. Priority → Next button), intercept Tab too
    if (e.key === "Tab" && onEnter) {
      e.preventDefault();
      setIsOpen(false);
      onEnter();
      return;
    }

    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setIsOpen(true);
        setCursorMoved(true);
      } else if (e.key === "Enter") {
        fireEnter();  // dropdown closed → go next field
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.min(f + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursorMoved(true);
      setFocused((f) => Math.max(f - 1, 0));
    } else if (e.key === "Escape") {
      setIsOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (cursorMoved && filtered.length > 0) {
        const target = filtered[focused] || filtered[0];
        if (target) choose(target);
      } else if (filtered.length === 1) {
        choose(filtered[0]);
      } else {
        setIsOpen(false);
      }
      setTimeout(fireEnter, 20);  // go next field after selecting
    }
  }

  return (
    <div className="cb-ac" ref={wrapRef}>
      <div className={`cb-ac-box${isOpen ? " open" : ""}`}>
        <input
          ref={inputRef}
          id={inputId}
          className="cb-ac-in"
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          onFocus={() => { setIsOpen(true); setCursorMoved(false); }}
          onClick={() => { if (!isOpen) setIsOpen(true); }}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); setFocused(-1); }}
          onKeyDown={handleKeyDown}
        />
        {value && (
          <span className="cb-ac-x on" onMouseDown={(e) => { e.preventDefault(); clear(); }}>×</span>
        )}
        <span className="cb-ac-chev" onMouseDown={(e) => { e.preventDefault(); setIsOpen((o) => !o); }}>▾</span>
      </div>
      {isOpen && (
        <div className="cb-ac-drop open">
          {filtered.length === 0 ? (
            <div className="cb-ac-empty">No matches found</div>
          ) : (
            filtered.map((opt, i) => (
              <div
                key={opt.value}
                ref={el => { optionRefs.current[i] = el; }}
                className={`cb-ac-opt${opt.value === value ? " sel" : ""}${i === focused ? " foc" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
                onMouseEnter={() => setFocused(i)}
              >
                {hl(opt.label, query)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}




// ── Enter key → next field for plain inputs ──

/* ══════════════════════════════════════════════════
   TOGGLE SWITCH
══════════════════════════════════════════════════ */
function Toggle({ checked, onChange, disabled }) {
  return (
    <label className={`cb-sw${disabled ? " cb-sw--disabled" : ""}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="cb-sw-track" />
    </label>
  );
}

/* ══════════════════════════════════════════════════
   TOAST SYSTEM
══════════════════════════════════════════════════ */
function ToastContainer({ toasts, onDismiss }) {
  return (
    <div className="cb-toast-wrap">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`cb-toast t-${t.type}`}
          onClick={() => onDismiss(t.id)}
          role="button"
          title="Click to dismiss"
        >
          <span style={{ fontSize: 14 }}>{t.type === "ok" ? "✓" : t.type === "err" ? "✕" : t.type === "warn" ? "⚠" : "ℹ"}</span>
          <span style={{ flex: 1 }}>{t.msg}</span>
          <span
            className="cb-toast-x"
            onClick={(e) => { e.stopPropagation(); onDismiss(t.id); }}
            aria-label="Dismiss"
          >✕</span>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   PIECE ROW
══════════════════════════════════════════════════ */
function PieceRow({ idx, row, divisor, onChange, onDelete }) {
  const vol = divisor ? ((row.l * row.w * row.h) / divisor) * row.qty : 0;
  const act = row.actW * row.qty;
  const chg = Math.max(act, vol);
  return (
    <tr>
      <td>{idx + 1}</td>
      <td><input id={`cb-piece-${idx}-desc`} type="text" value={row.desc} onChange={(e) => onChange({ ...row, desc: e.target.value })} style={{ minWidth: 120 }} /></td>
      <td>
        <select id={`cb-piece-${idx}-pkgType`} value={row.pkgType} onChange={(e) => onChange({ ...row, pkgType: e.target.value })}>
          {PKG_TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </td>
      <td><input id={`cb-piece-${idx}-qty`} type="number" value={row.qty} min={1} onChange={(e) => onChange({ ...row, qty: parseFloat(e.target.value) || 0 })} style={{ width: 55 }} /></td>
      <td><input type="number" value={row.l} min={0} onChange={(e) => onChange({ ...row, l: parseFloat(e.target.value) || 0 })} style={{ width: 64 }} /></td>
      <td><input type="number" value={row.w} min={0} onChange={(e) => onChange({ ...row, w: parseFloat(e.target.value) || 0 })} style={{ width: 64 }} /></td>
      <td><input type="number" value={row.h} min={0} onChange={(e) => onChange({ ...row, h: parseFloat(e.target.value) || 0 })} style={{ width: 64 }} /></td>
      <td><input id={`cb-piece-${idx}-actW`} type="number" value={row.actW} min={0} step={0.1} onChange={(e) => onChange({ ...row, actW: parseFloat(e.target.value) || 0 })} style={{ width: 76 }} /></td>
      <td className="cb-vol-cell">{vol > 0 ? vol.toFixed(2) + " kg" : "—"}</td>
      <td className="cb-chg-cell">{chg > 0 ? chg.toFixed(2) + " kg" : "—"}</td>
      <td><button className="cb-del-btn" onClick={onDelete}>✕</button></td>
    </tr>
  );
}

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
export default function ConsignmentBooking({ variant = "full" }) {
  // variant: "full" (desktop / "Fully Version" menu) or "mobile" ("Mobile Version" menu)
  // Use `isMobile` anywhere below to conditionally hide a field or section:
  //   {!isMobile && ( ...field or section JSX... )}
  // To hide MORE fields later, wrap them the same way — no state/logic changes needed.
  const isMobile = variant === "mobile";

  const today = new Date();
  const todayStr = fmtDate(today);

  /* ── Consignment No ── */
  const [cnr, setCnr] = useState("");
  const [cnrDuplicate, setCnrDuplicate] = useState(false);   // true if this CNR already exists in consignment_master
  const [cnrChecking, setCnrChecking] = useState(false);     // true while the duplicate check is in flight

  /* ── Edit mode ──
     Set only when a row is loaded via editSearchRow (Search window ➜ Edit
     icon). Carries the existing row's Consignment_Id so doBook() can send it
     back to /save-cons-booking. The backend (ConsBookingController.
     SaveConsBooking) branches on this: Consignment_Id > 0 ⇒ UPDATE the
     existing row (Book_Time included) keyed by that id; 0/null ⇒ INSERT a
     new booking. Without this, editing and re-submitting silently created a
     duplicate new consignment instead of updating the one being edited.
     Reset to null by clearForm()/newBooking() so the next booking is a
     fresh insert again. */
  const [editingConsignmentId, setEditingConsignmentId] = useState(null);

  /* ── Step ── */
  const [step, setStep] = useState(1);
  // Same logic as CVBookingForm.cshtml's $(document).ready currentDate/currentTime:
  // local date as YYYY-MM-DD, local 24-hour time with seconds as HH:MM:SS.
  const [bookingDate, setBookingDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [bookingTime] = useState(() => new Date().toLocaleTimeString('en-CA', { hour12: false }));

  /* ── Route ── */
  const [bookingPlace, setBookingPlace] = useState("");
  const [bookingPin, setBookingPin] = useState("");
  const [bpinInfo, setBpinInfo] = useState("");
  const [deliveryPlace, setDeliveryPlace] = useState("");
  const [deliveryPin, setDeliveryPin] = useState("");
  const [dpinInfo, setDpinInfo] = useState("");
  const [priority, setPriority] = useState("");
  const [expDelivery, setExpDelivery] = useState("");

  /* ── Party ── */
  const [partyKey, setPartyKey] = useState("");
  const [partyGst, setPartyGst] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [freight, setFreight] = useState("");
  const [partyJobs, setPartyJobs] = useState([]);

  /* ── Consignor ── */
  const emptyParty = { name: "", contact: "", mobile: "", addr: "", gst: "", pin: "", email: "" };
  const [cnorSel, setCnorSel] = useState("");
  const [cnorData, setCnorData] = useState(emptyParty);
  const [cnorMode, setCnorMode] = useState("list");

  /* ── Consignee ── */
  const [cneeSel, setCneeSel] = useState("");
  const [cneeData, setCneeData] = useState(emptyParty);
  const [cneeMode, setCneeMode] = useState("list");

  /* ── OTP ── */
  const [otpOn, setOtpOn] = useState(false);
  const [otpMobile, setOtpMobile] = useState("");
  const [jobOtpMandatory, setJobOtpMandatory] = useState(false);
  const [otpChannel, setOtpChannel] = useState("both");
  const [otpValidity, setOtpValidity] = useState("30");
  const [otpAttempts, setOtpAttempts] = useState("3");

  /* ── Notifications ── */
  const [notifMaster, setNotifMaster] = useState(false);
  const [smsOn, setSmsOn] = useState(true);
  const [emailOn, setEmailOn] = useState(true);
  const [waOn, setWaOn] = useState(true);
  const [smsNum, setSmsNum] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [waNum, setWaNum] = useState("");
  // Consignee's OWN notification targets — separate from the Consignor
  // fields above. Same on/off toggle per channel (smsOn/emailOn/waOn) and
  // same per-channel event checklist, but a distinct number/address so
  // Consignor and Consignee can be notified at two different contacts.
  // Sent to the backend as Cnee_Sms_Number / Cnee_Wa_Number /
  // Cnee_Email_Address, gated by Send_*_Notification_Consignee.
  const [cneeSmsNum, setCneeSmsNum] = useState("");
  const [cneeEmailAddr, setCneeEmailAddr] = useState("");
  const [cneeWaNum, setCneeWaNum] = useState("");
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [notificationEvents, setNotificationEvents] = useState([]);

  // Sync the master SMS/WhatsApp/Email toggles to actually match
  // ChannelMaster's IsEnabled flag, once notificationEvents has loaded.
  // /get-standard-notifications only returns events for channels where
  // IsEnabled = true (same filtering as /get-channels), so a channelId
  // with zero events here means that channel is currently disabled.
  // Previously these three toggles were just hardcoded to a fixed default,
  // completely independent of the actual ChannelMaster setting — this is
  // what made a disabled channel (e.g. Email) show as ON, and an enabled
  // one (e.g. WhatsApp) show as OFF, regardless of the DB.
  useEffect(() => {
    if (!notificationEvents || notificationEvents.length === 0) return; // not loaded yet, keep defaults
    setSmsOn(notificationEvents.some(e => e.channelId === 1));
    setWaOn(notificationEvents.some(e => e.channelId === 2));
    setEmailOn(notificationEvents.some(e => e.channelId === 3));
  }, [notificationEvents]);
  const [events, setEvents] = useState([]);

  /* ── Pieces ── */

    const [isVolumetric, setIsVolumetric] = useState(false);
  const [shipMode, setShipMode] = useState("");
  // Air/Surface volumetric divisor for the currently selected Job, fetched
  // from GET /get-job-charged-weights/{job_id} (JOB_Master.chrgwtAir / chrgwtSur).
  // null until fetched / if the job has no value configured.
  const [jobDivisors, setJobDivisors] = useState({ chrgwtAir: null, chrgwtSur: null });
  const [jobDivisorsLoading, setJobDivisorsLoading] = useState(false);
  const [manualPcs, setManualPcs] = useState(1);
  const [manualChgWt, setManualChgWt] = useState(1);
  const [pieces, setPieces] = useState([]);
  const [pidCtr, setPidCtr] = useState(1);
  const [commodity, setCommodity] = useState("");
  const [declVal, setDeclVal] = useState("");
  const [specialInst, setSpecialInst] = useState("");

  /* ── Rate ── */
  const [rateBasis, setRateBasis] = useState("perkg");
  const [baseRate, setBaseRate] = useState(0);
  const [rateEdited, setRateEdited] = useState(false); // true once user manually overrides a slab-matched rate
  const [fuelPct, setFuelPct] = useState(12);
  const [gstPct, setGstPct] = useState("5");
  const [insAmt, setInsAmt] = useState(0);
  const [otherAmt, setOtherAmt] = useState(0);

  /* ── Fetched Rate Details (from API) ── */
  const [fetchedRateHeader, setFetchedRateHeader] = useState(null);
  const [fetchedRateDetails, setFetchedRateDetails] = useState([]);
  const [rateLoading, setRateLoading] = useState(false);

  /* ── Images ── */
  const [images, setImages] = useState([]);
  const [imgType, setImgType] = useState("Package");

  /* ── API-driven dropdown options ── */
  const [bookingPlaceOpts, setBookingPlaceOpts] = useState([]);
  const [deliveryPlaceOpts, setDeliveryPlaceOpts] = useState([]);
  const [partyOpts, setPartyOpts] = useState([]);
  const [cnorOpts, setCnorOpts] = useState([]);
  const [cneeOpts, setCneeOpts] = useState([]);
  const [allPartiesData, setAllPartiesData] = useState({});

  /* ══════════════════════════════════════════════════
     SEARCH WINDOW — Branch / Party / Date range
     (search-cons-bookings + delete-cons-booking)
  ══════════════════════════════════════════════════ */
  // Mobile Version has no Search Window at all (no branch/party/date-range
  // filters, no Booking Results list, no edit/preview/delete row actions —
  // that whole page is desktop-only). So mobile skips straight to the
  // booking form instead of landing on "search" like the Full Version does.
  const [view, setView] = useState(isMobile ? "form" : "search"); // "search" (default landing, full version only) | "form" (booking form)
  const [searchBranchId, setSearchBranchId] = useState("");
  const [searchPartyId, setSearchPartyId] = useState("");
  const [searchJobId, setSearchJobId] = useState(""); // Job / Department filter — party-wise, same list as the booking form's Job dropdown
  const [searchPartyOpts, setSearchPartyOpts] = useState([]); // GET /get-partylistBranchWise (or /get-partylist when no Branch chosen) — Search filters' own Party list, kept separate from the booking form's branch-driven partyOpts
  const [searchPartyJobs, setSearchPartyJobs] = useState([]); // GET /get-job-list/{party_id} result for whichever Party is picked in the search filters
  const [searchPartyJobsLoading, setSearchPartyJobsLoading] = useState(false);
  const [searchBillStatus, setSearchBillStatus] = useState(""); // "" (All) | "billed" | "unbilled"
  const [searchConsignmentNo, setSearchConsignmentNo] = useState("");
  const [searchFromDate, setSearchFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [searchToDate, setSearchToDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [searchRows, setSearchRows] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);   // true only for a fresh search (spinner replaces table)
  const [searchLoadingMore, setSearchLoadingMore] = useState(false); // true while fetching the next batch (spinner at bottom, table stays visible)
  const [searchDone, setSearchDone] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const SEARCH_PAGE_SIZE = 100; // batch size per fetch — keeps each request light even with 5000+ matching rows
  const [searchTotalCount, setSearchTotalCount] = useState(0);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const searchScrollRef = useRef(null);
  const [searchPreviewRow, setSearchPreviewRow] = useState(null);
  const [searchPreviewLoading, setSearchPreviewLoading] = useState(false);
  const [searchDeleteRow, setSearchDeleteRow] = useState(null);
  const [searchDeleting, setSearchDeleting] = useState(false);
  const [freightTermOpts, setFreightTermOpts] = useState([]);
  const [priorityOpts, setPriorityOpts] = useState([]);

  /* ── SEARCH WINDOW: Job dropdown is party-wise (same as the Booking Form) —
     whenever the Party filter changes, reload that party's Job list and clear
     any previously-selected Job so it can't linger and silently filter results
     against the wrong party. ── */
  useEffect(() => {
    setSearchJobId("");
    if (!searchPartyId) { setSearchPartyJobs([]); return; }
    let cancelled = false;
    setSearchPartyJobsLoading(true);
    (async () => {
      try {
        const res = await api.get(`/get-job-list/${searchPartyId}`);
        if (!cancelled) setSearchPartyJobs(res.data || []);
      } catch (err) {
        console.error("Failed to fetch jobs for search filter:", err);
        if (!cancelled) setSearchPartyJobs([]);
      } finally {
        if (!cancelled) setSearchPartyJobsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [searchPartyId]);

  const searchJobOpts = searchPartyJobs.map(j => ({
    value: j.job_Id ?? j.Job_Id,
    label: j.job_Name ?? j.Job_Name,
  }));

  /* ── Modals ── */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stickerOpen, setStickerOpen] = useState(false);
  const [stickerData, setStickerData] = useState({});
  const [successOpen, setSuccessOpen] = useState(false);
  const [bookedCnr, setBookedCnr] = useState("");

  /* ── Post-booking notification modal ── */
  const [notifModal, setNotifModal]     = useState({ open: false, email: false, wa: false });
  const [notifSending, setNotifSending] = useState({ email: false, wa: false });
  const [notifSent,    setNotifSent]    = useState({ email: false, wa: false });

  // Lock background scroll while any modal overlay is open, so the
  // fixed-position overlay always renders centered in the viewport
  // instead of being clipped/misaligned by the page's own scroll position.
  useEffect(() => {
    const anyModalOpen = confirmOpen || successOpen || stickerOpen || notifModal.open;
    if (anyModalOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prevOverflow; };
    }
  }, [confirmOpen, successOpen, stickerOpen, notifModal.open]);

  // Auto-focus the "Confirm & Book" button as soon as the Confirm Booking
  // modal opens, so the user can press Enter/Space right away instead of
  // having to Tab to it. Retries briefly since the modal mounts at the same
  // time as the smooth scroll-to-top, which can otherwise win the race.
  useEffect(() => {
    if (!confirmOpen) return;
    const timers = [80, 200, 400].map(delay =>
      setTimeout(() => {
        try {
          const btn = document.getElementById("cb-confirm-modal-book-btn");
          // preventScroll stops the browser from auto-scrolling the button into
          // view on focus, which was dragging the modal up and showing it half-cut
          if (btn && document.activeElement !== btn) btn.focus({ preventScroll: true });
        } catch (err) {
          // ignore — focusing is a nice-to-have, never break the modal
        }
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [confirmOpen]);

  // Auto-focus the "+ New Booking" button as soon as the booking-success
  // modal opens, so a plain Enter press immediately clears the form and
  // starts the next booking instead of requiring a mouse click.
  useEffect(() => {
    if (!successOpen) return;
    const timers = [80, 200, 400].map(delay =>
      setTimeout(() => {
        try {
          const btn = document.getElementById("cb-success-newbooking-btn");
          if (btn && document.activeElement !== btn) btn.focus({ preventScroll: true });
        } catch (err) {
          // ignore — focusing is a nice-to-have, never break the modal
        }
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [successOpen]);

  // Editable fields inside notif modal — prefilled from consignor
  const [notifEmailVal, setNotifEmailVal] = useState("");
  const [notifWaNum,    setNotifWaNum]    = useState("");
  // The CNR that was JUST booked — captured before fetchAvailableConsignments refreshes cnr
  const [bookedCnrForNotif, setBookedCnrForNotif] = useState("");
  const [bookedStickerData, setBookedStickerData] = useState({});
  const [availableConsignments, setAvailableConsignments] = useState([]);
  const [cnrLoading, setCnrLoading] = useState(false);
  const [cnrEdited, setCnrEdited] = useState(false); // true once user manually edits the fetched CNR
  const [rawConsNo, setRawConsNo] = useState(null); // pure numeric ID for payload
  const [rawPrefix, setRawPrefix] = useState("");   // prefix for payload

  /* ── Toast & booking state ── */
  const [toasts, setToasts] = useState([]);
  const [isBooking, setIsBooking] = useState(false);
  const barcodeSvgRef = useRef(null);
  const stepperRef = useDraggableScroll();



  
  /* ══════════════════════════════════════════════════
     GLOBAL ENTER-KEY → NEXT FIELD NAVIGATION
     Works for: input (text/number/date/tel), select, textarea
     Textarea: Enter moves to next field, Shift+Enter inserts newline
  ══════════════════════════════════════════════════ */
  function handleEnterKey(e) {
    if (e.key !== "Enter") return;

    const tag = e.target.tagName.toLowerCase();
    const type = (e.target.type || "").toLowerCase();

    // Skip buttons — let them do their normal action
    if (tag === "button") return;

    // Skip checkboxes — Enter should toggle them normally
    if (type === "checkbox" || type === "radio") return;

    // Skip if inside AutoComplete dropdown (it handles its own Enter)
    const acWrap = e.target.closest(".cb-ac");
    if (acWrap) {
      const dropdown = acWrap.querySelector(".cb-ac-drop.open");
      if (dropdown) return; // let AutoComplete handleKeyDown manage it
    }

    // Textarea: Shift+Enter = newline, plain Enter = next field
    if (tag === "textarea" && e.shiftKey) return;

    e.preventDefault();

    // Build list of all visible focusable fields (same order as DOM)
    const focusable = Array.from(
      document.querySelectorAll(
        'input:not([disabled]):not([readonly]):not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), '
        + 'select:not([disabled]), '
        + 'textarea:not([disabled]):not([readonly]), '
        + 'button:not([disabled])'
      )
    ).filter(el => el.offsetParent !== null); // only visible elements

    const idx = focusable.indexOf(e.target);
    if (idx !== -1 && idx < focusable.length - 1) {
      focusable[idx + 1].focus();
    }
  }
  /* ══════════════════════════════════════════════════
     TOAST
  ══════════════════════════════════════════════════ */
  function dismissToast(id) {
    setToasts((t) => t.filter((x) => x.id !== id));
  }
  function addToast(msg, type = "info") {
    const id = Date.now() + Math.random();
    // Guard against any invalid/empty type slipping through — without this,
    // the toast would render with no background color and be invisible.
    const VALID_TYPES = ["ok", "err", "warn", "info"];
    const safeType = VALID_TYPES.includes(type) ? type : "info";
    // Longer dwell time so messages can actually be read; errors/warnings
    // stay up longest since they matter most. User can also click to
    // dismiss any toast early via the X button or the toast itself.
    const DURATIONS = { err: 8000, warn: 7000, ok: 5500, info: 5000 };
    const duration = DURATIONS[safeType] || 5000;
    setToasts((t) => [...t, { id, msg, type: safeType }]);
    setTimeout(() => dismissToast(id), duration);
  }

  


  /* ══════════════════════════════════════════════════
     NOTIFICATION HELPERS
  ══════════════════════════════════════════════════ */
  const syncPartyNotifications = useCallback((partyId) => {
    if (!partyId) return;
    const d = allPartiesData[partyId];
    if (d) {
      // Underlying values still sync from the party's saved preferences, so
      // they're correct whenever the user opens this panel themselves — but
      // we no longer force notifMaster to true here. The panel stays
      // collapsed by default until the user explicitly clicks the ON
      // switch, even if this party already has saved notification prefs.
      setSmsOn(d.notifSms);
      setEmailOn(d.notifEmail);
      setWaOn(d.notifWa);
      setSmsNum(d.smsNumber);
      setEmailAddr(d.emailAddress);
      setWaNum(d.waNumber);
      setSelectedEvents(d.selectedEvents || []);
    }
  }, [allPartiesData]);

  // The shared notification panel (Mobile No./WhatsApp No./Email Address)
  // represents the CONSIGNOR going forward — whenever Consignor's mobile/email
  // are populated (either by picking one From List via handleCnorSel, or by
  // typing/correcting them in Direct Entry), mirror them into the panel's
  // fields. This does NOT touch the ON/OFF toggles (notifMaster/smsOn/waOn/emailOn)
  // — only the target number/address. syncPartyNotifications() (called from
  // handleCnorSel) already seeds these from the party master on selection;
  // this effect keeps them in sync afterward too, e.g. if the user
  // hand-edits the Consignor's mobile/email in Direct Entry.
  //
  // Consignee does NOT feed this panel anymore. It used to (see git history),
  // which meant picking a Consignee after a Consignor silently overwrote the
  // Consignor's number here — so a "Booking Confirmed for Consignor" event
  // would fire to the Consignee's number instead. Consignee now sends
  // independently via Send_Sms/Wa/Email_Notification_Consignee, straight off
  // Consignee_Phone/consignee_Email (see the payload in doBook()) — so both
  // recipients get notified correctly, on their own numbers, based on
  // whichever events are checked for each.
  useEffect(() => {
    if (cnorData.mobile) {
      setSmsNum(cnorData.mobile);
      setWaNum(cnorData.mobile);
    }
    if (cnorData.email) {
      setEmailAddr(cnorData.email);
    }
  }, [cnorData.mobile, cnorData.email]);

  // Mirror of the effect above, but for the CONSIGNEE's own dedicated
  // notification fields (cneeSmsNum/cneeWaNum/cneeEmailAddr) — NOT the
  // shared panel above (that stays Consignor-only, see the comment above).
  // Without this, Cnee_Sms_Number/Cnee_Wa_Number/Cnee_Email_Address in the
  // payload stay empty unless someone manually retypes the Consignee's
  // number/email a second time in the Notifications panel — which isn't
  // even shown on Mobile Version. This keeps them synced from whatever the
  // Consignee's mobile/email fields already have (Address Book or Direct
  // Entry, on either Desktop or Mobile).
  useEffect(() => {
    if (cneeData.mobile) {
      setCneeSmsNum(cneeData.mobile);
      setCneeWaNum(cneeData.mobile);
    }
    if (cneeData.email) {
      setCneeEmailAddr(cneeData.email);
    }
  }, [cneeData.mobile, cneeData.email]);

  // Get channel name by channelId from notificationEvents
  const getTitle = (channelId, fallback) => {
    const ev = notificationEvents?.find(e => e.channelId === channelId);
    return ev ? (ev.channelName || fallback) : fallback;
  };

  // notify.Event now carries recipient-specific rows (EventCode suffixed
  // _CE for Consignee / _CR for Consignor — see notify_event_consignee_consignor.sql).
  // Older/unmigrated rows have no suffix, so we treat those as shared
  // between both panels rather than hiding them.
  const isBookingEvent = (e) => {
    const code = (e.eventCode || "").toUpperCase();
    return code === "BOOKING" || code.startsWith("BOOKING_");
  };
  const matchesRecipient = (e, suffix) => {
    const code = (e.eventCode || "").toUpperCase();
    if (code.endsWith("_CE") || code.endsWith("_CR")) return code.endsWith(suffix);
    return true; // legacy/unsuffixed code — show on both panels
  };

  // Get events filtered by channelId, optionally narrowed to a recipient
  // ("_CE" for the Consignee panel, "_CR" for the Consignor panel).
  const getEventsByChannel = (channelId, recipientSuffix = null) => {
    return (notificationEvents || []).filter(e =>
      e.channelId === channelId && (!recipientSuffix || matchesRecipient(e, recipientSuffix))
    );
  };

  const toggleEvent = (eventId) => {
    setSelectedEvents(prev =>
      prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]
    );
  };

  /* ══════════════════════════════════════════════════
     INIT
  ══════════════════════════════════════════════════ */
  useEffect(() => {
    fetchPlaces();
    fetchParties();
    fetchAllParties();
    fetchNotificationEvents();
    //fetchEvents();
    fetchFreightTerms();
    fetchPriorities();
    fetchAvailableConsignments();
    runBookingSearch(); // default landing view is the search screen
    setExpDelivery(calcExpDeliveryDate("standard", bookingDate));
    setPieces([
      { id: 1, desc: "Engine Parts", pkgType: "Box / Carton", qty: 2, l: 60, w: 40, h: 35, actW: 12.5 },
      { id: 2, desc: "Brake Pads",   pkgType: "Box / Carton", qty: 1, l: 30, w: 20, h: 15, actW: 3.0 },
    ]);
    setPidCtr(3);
    // Land the cursor on Consignment Number by default so the user can
    // start typing / Tab straight through the form.
    setTimeout(() => {
      document.getElementById("cb-cnr-input")?.focus();
    }, 80);
    // eslint-disable-next-line
  }, []);

  /* ── Mobile Version: this screen doesn't show Freight Terms, Priority,
     or the Consignor/Consignee "From List vs Direct Entry" toggle — but
     those are still required by validateStep1/2 and the backend payload.
     So for mobile we quietly default them instead of asking the user:
     - Consignor/Consignee always use Direct Entry (just the Name field)
     - Freight Terms / Priority default to whatever option the backend
       lists first, as soon as those lists load */
  useEffect(() => {
    if (!isMobile) return;
    setCnorMode("direct");
    setCneeMode("direct");
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (!freight && freightTermOpts.length > 0) {
      const first = freightTermOpts[0];
      setFreight(first.freight_Terms_Id ?? first.id ?? first.value ?? "");
    }
  }, [isMobile, freight, freightTermOpts]);

  useEffect(() => {
    if (!isMobile) return;
    if (!priority && priorityOpts.length > 0) {
      const first = priorityOpts[0];
      setPriority(first.priority_ID ?? first.id ?? first.Priority_ID ?? "");
    }
  }, [isMobile, priority, priorityOpts]);

  // Mobile Version's Submit button — validates the same way as the desktop
  // "Confirm & Book" button, but skips the review modal and books directly.
  function handleMobileSubmit() {
    const error = validateForm();
    if (error) { reportFieldError(error); return; }
    doBook();
  }

  /* Auto-focus first field whenever a step is entered */
  useEffect(() => {
    if (step === 1) {
      setTimeout(() => {
        document.getElementById("cb-cnr-input")?.focus();
      }, 80);
    }
    if (step === 2) {
      // Only force both panels to "From List" for a brand-new booking. When
      // editing an existing one, editSearchRow already set cnorMode/cneeMode
      // correctly based on whether Consignor_Id/Consignee_Id came back from
      // the server (List) or null (Direct Entry) — unconditionally resetting
      // to "list" here on every visit to step 2 was wiping that out and
      // stranding Direct Entry bookings on an empty "From List" search box.
      if (!editingConsignmentId) {
        setCnorMode("list");
        setCneeMode("list");
      }
      setTimeout(() => {
        const cnorInput = document.getElementById("cb-cnor-ac-input");
        if (cnorInput) cnorInput.focus();
      }, 80);
    }
    if (step === 3) {
      setTimeout(() => {
        const commodityInput = document.getElementById("cb-commodity-input");
        if (commodityInput) commodityInput.focus();
      }, 80);
    }
  }, [step, editingConsignmentId]);

  const fetchAvailableConsignments = async () => {
    setCnrLoading(true);
    try {
      const res = await api.get('/get-available-consignments');
      const item = res.data;
      const consNo = item?.Consignment_No ?? item?.consignment_No ?? item?.consignmentNo ?? null;
      const prefix  = item?.prefix ?? item?.Prefix ?? "";
      if (consNo != null) {
        const display = prefix ? `${prefix}-${consNo}` : String(consNo);
        setCnr(display);
        setRawConsNo(consNo);
        setRawPrefix(prefix);          // store prefix separately for payload
        setAvailableConsignments([item]);
        setCnrEdited(false);           // freshly fetched value, not manually overridden
      } else {
        setCnr("");
        setRawConsNo(null);
        setRawPrefix("");
        setAvailableConsignments([]);
        setCnrEdited(false);
      }
    } catch (err) {
      console.error("Failed to fetch available consignments:", err);
      addToast("Failed to load consignment number", "err");
    } finally {
      setCnrLoading(false);
    }
  };

  // Called whenever the user types directly into the Consignment Number field.
  // Only digits are allowed — any letter/symbol the user types is stripped
  // immediately so the field can never hold non-numeric characters.
  const handleCnrChange = (val) => {
    const numericVal = val.replace(/\D/g, "");
    setCnr(numericVal);
    setCnrEdited(true);
    setRawPrefix("");
    // rawConsNo must be a real number, not a string — Consignment_No on the
    // backend is a numeric column. The auto-fetch path (fetchAvailableConsignments,
    // above) already stores a number straight from the API response, but this
    // manual-entry path was storing the raw regex-stripped STRING instead
    // (val.replace() always returns a string). A quoted "238746" in the JSON
    // body fails to deserialize into a numeric Consignment_No, which breaks
    // binding of the whole requestList → "No records provided." for every
    // manually-typed CNR. parseInt fixes that; empty input stays null.
    setRawConsNo(numericVal ? parseInt(numericVal, 10) : null);
    setCnrDuplicate(false); // stale result — re-check on blur once they stop typing
  };

  // Hits the backend to see if this Consignment No already has a booking
  // against it in [dbo].[consignment_master]. Runs on blur (not on every
  // keystroke) so it doesn't hammer the API while the user is still typing.
  // Returns true/false (duplicate or not) so the Enter/Tab handler can
  // decide, right then, whether it's safe to move focus to the next field.
  const checkCnrDuplicate = async (value) => {
    const consNo = (value ?? cnr ?? "").trim();
    if (!consNo) { setCnrDuplicate(false); return false; }
    // While editing an existing booking, this Consignment No is SUPPOSED to
    // already exist (it's the row being updated) — hitting the availability
    // check here would flag it as a false-positive duplicate and block the
    // save. Only run this check for a brand-new booking.
    if (editingConsignmentId) { setCnrDuplicate(false); return false; }
    setCnrChecking(true);
    try {
      const res = await api.get(`/check-consignment-no/${encodeURIComponent(consNo)}`);
      const isDup = !!res.data?.exists;
      setCnrDuplicate(isDup);
      return isDup;
    } catch (err) {
      console.error("Failed to check consignment number:", err);
      // Fail open — don't block booking just because the availability check itself errored
      setCnrDuplicate(false);
      return false;
    } finally {
      setCnrChecking(false);
    }
  };

  const fetchPlaces = async () => {
    try {
      const bookRes = await api.get('/get-booking-places');
      setBookingPlaceOpts(bookRes.data.map(b => ({
        value: b.place_Id || b.Place_Id,
        label: b.place_name || b.Place_name,
        pincode: b.pincode || b.Pincode
      })));
      const delRes = await api.get('/get-delivery-places');
      setDeliveryPlaceOpts(delRes.data.map(b => ({
        value: b.place_Id || b.Place_Id,
        label: b.place_name || b.Place_name,
        pincode: b.pincode || b.Pincode
      })));
    } catch (err) {
      console.error("Failed to fetch places:", err);
      addToast("Failed to fetch places", "err");
    }
  };

  // GET /get-partylistBranchWise?billingBranchId=X — used ONLY by the Search
  // window's Party filter (see below). The main Booking Form's Party dropdown
  // stays as the plain unfiltered list, same as before — branch-wise scoping
  // is a Search-only filter, not a change to how you pick a Party when
  // actually booking.
  const fetchPartiesByBranch = async (branchId) => {
    const res = await api.get('/get-partylistBranchWise', { params: { billingBranchId: branchId } });
    return (res.data || []).map(p => ({
      value: p.party_id ?? p.Party_id,
      label: p.party_Name ?? p.Party_Name
    }));
  };

  // Unfiltered (all branches) — Booking Form's Party dropdown, fetched once
  // on mount, exactly as it always was. Also the Search window's fallback
  // while "All Branches" is selected there.
  const fetchAllPartiesFlat = async () => {
    const res = await api.get('/get-partylist');
    return (res.data || []).map(p => ({
      value: p.party_id || p.Party_id,
      label: p.party_Name || p.Party_Name
    }));
  };

  const fetchParties = async () => {
    try {
      setPartyOpts(await fetchAllPartiesFlat());
    } catch (err) { console.error("Failed to fetch parties:", err); }
  };

  /* ── SEARCH WINDOW: Party dropdown is branch-wise — reload on Branch
     filter change; falls back to the unfiltered list while "All Branches" is
     selected, since that filter is optional there. ── */
  useEffect(() => {
    setSearchPartyId(""); // previous branch's party selection no longer applies
    let cancelled = false;
    (async () => {
      try {
        let opts;
        if (searchBranchId) {
          opts = await fetchPartiesByBranch(searchBranchId);
        } else {
          opts = await fetchAllPartiesFlat();
        }
        if (!cancelled) setSearchPartyOpts(opts);
      } catch (err) {
        console.error("Failed to fetch parties for search filter:", err);
        if (!cancelled) setSearchPartyOpts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [searchBranchId]);

  /* ── SEARCH WINDOW: run search ── */
  // Fresh search — always starts back at page 1 and replaces the current rows.
  // Called on mount, on filter Search click, and on Reset.
  const runBookingSearch = useCallback(async () => {
    setSearchLoading(true);
    setSearchError("");
    setSearchDone(true);
    try {
      const params = {
        page: 1,
        pageSize: SEARCH_PAGE_SIZE,
      };
      if (searchBranchId) params.branchId = searchBranchId;
      if (searchPartyId) params.partyId = searchPartyId;
      if (searchJobId) params.jobId = searchJobId;
      if (searchBillStatus) params.billStatus = searchBillStatus;
      if (searchConsignmentNo.trim()) params.consignmentNo = searchConsignmentNo.trim();
      if (searchFromDate) params.fromDate = searchFromDate;
      if (searchToDate) params.toDate = searchToDate;

      const res = await api.get('/search-cons-bookings', { params });
      const body = res.data || {};
      setSearchRows(body.data || []);
      setSearchPage(1);
      setSearchTotalCount(body.totalCount ?? (body.data || []).length);
      setSearchHasMore(!!body.hasMore);
      // Snap the results panel back to the top for a fresh search
      if (searchScrollRef.current) searchScrollRef.current.scrollTop = 0;
    } catch (err) {
      console.error("Booking search failed:", err);
      setSearchRows([]);
      setSearchHasMore(false);
      setSearchError(err.response?.data?.message || "Failed to search bookings. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  }, [searchBranchId, searchPartyId, searchJobId, searchBillStatus, searchConsignmentNo, searchFromDate, searchToDate]);

  // Batch/chunk-wise loading — fetches the NEXT page and appends it to what's
  // already on screen. This is what lets a 5000+ row result set load smoothly:
  // only SEARCH_PAGE_SIZE rows are ever requested at a time.
  const loadMoreSearchResults = useCallback(async () => {
    if (searchLoading || searchLoadingMore || !searchHasMore) return;
    const nextPage = searchPage + 1;
    setSearchLoadingMore(true);
    try {
      const params = {
        page: nextPage,
        pageSize: SEARCH_PAGE_SIZE,
      };
      if (searchBranchId) params.branchId = searchBranchId;
      if (searchPartyId) params.partyId = searchPartyId;
      if (searchJobId) params.jobId = searchJobId;
      if (searchBillStatus) params.billStatus = searchBillStatus;
      if (searchConsignmentNo.trim()) params.consignmentNo = searchConsignmentNo.trim();
      if (searchFromDate) params.fromDate = searchFromDate;
      if (searchToDate) params.toDate = searchToDate;

      const res = await api.get('/search-cons-bookings', { params });
      const body = res.data || {};
      setSearchRows(prev => [...prev, ...(body.data || [])]);
      setSearchPage(nextPage);
      setSearchTotalCount(body.totalCount ?? searchTotalCount);
      setSearchHasMore(!!body.hasMore);
    } catch (err) {
      console.error("Failed to load next batch:", err);
      addToast("Failed to load more bookings.", "err");
    } finally {
      setSearchLoadingMore(false);
    }
  }, [searchLoading, searchLoadingMore, searchHasMore, searchPage, searchTotalCount, searchBranchId, searchPartyId, searchJobId, searchBillStatus, searchConsignmentNo, searchFromDate, searchToDate]);

  // Infinite-scroll trigger — fires when the results panel is scrolled near
  // its bottom, so the next batch loads just before the user reaches the end.
  const handleSearchResultsScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      loadMoreSearchResults();
    }
  };

  // "+ Add Booking" — switch to the booking form with a fresh CNR
  const startNewBooking = () => {
    setStep(1);
    setView("form");
    fetchAvailableConsignments();
  };

  const resetSearchFilters = () => {
    setSearchBranchId("");
    setSearchPartyId(""); // triggers the party-change effect, which also clears searchPartyJobs
    setSearchJobId("");
    setSearchBillStatus("");
    setSearchConsignmentNo("");
    const d = new Date(); d.setDate(d.getDate() - 30);
    setSearchFromDate(d.toISOString().split("T")[0]);
    setSearchToDate(new Date().toISOString().split("T")[0]);
  };


  const isSearchRowBilled = (row) => (row.bill_Id ?? row.Bill_Id ?? 0) !== 0;

  /* ── SEARCH WINDOW: preview a row ── */
  const openSearchPreview = async (row) => {
    setSearchPreviewRow({ ...row });
    setSearchPreviewLoading(true);
    try {
      const res = await api.get(`/get-cons-booking/${row.consignment_No ?? row.Consignment_No}`);
      if (res.data?.success) setSearchPreviewRow(res.data.data);
    } catch (err) {
      console.error("Preview fetch failed:", err);
    } finally {
      setSearchPreviewLoading(false);
    }
  };

  /* ── SEARCH WINDOW: load a row into the booking form for editing ──
     Routed through the same handlePlaceChange / handlePartyChange /
     handleJobChange / handlePriorityChange / handleCnorSel / handleCneeSel
     handlers the dropdowns themselves use, so every dependent field
     (pincode, GSTIN, job list, divisors) populates exactly as it would
     from a manual selection. Party is awaited first since the Job
     dropdown's options only exist once that party's job list has loaded. */
  /* ── SEARCH WINDOW: load a row into the booking form for editing ──
     The search-results row only carries a lean set of columns (fast for a
     5000-row list) — it doesn't have Dimension, notification numbers, OTP
     settings, etc. So this fetches the FULL record from get-cons-booking
     first, then maps every field it returns onto its matching form state,
     routed through the same handlePlaceChange / handlePartyChange /
     handleJobChange / handlePriorityChange / handleCnorSel / handleCneeSel
     handlers the dropdowns themselves use, so every dependent field
     (pincode, GSTIN, job list, divisors) populates exactly as it would
     from a manual selection. Party is awaited first since the Job
     dropdown's options only exist once that party's job list has loaded. */
  const editSearchRow = async (rowStub) => {
    const cnrNo = rowStub.consignment_No ?? rowStub.Consignment_No;
    let row = rowStub;
    try {
      const res = await api.get(`/get-cons-booking/${cnrNo}`);
      if (res.data?.success && res.data.data) row = res.data.data;
    } catch (err) {
      console.error("Failed to fetch full booking for edit — falling back to the search row:", err);
    }

    // Remember which existing row this is so doBook() sends Consignment_Id
    // back to the server — that's what makes /save-cons-booking UPDATE this
    // row (Book_Time included) instead of INSERTing a brand-new booking.
    const editId = Number(row.consignment_Id ?? row.Consignment_Id);
    setEditingConsignmentId(!Number.isNaN(editId) && editId > 0 ? editId : null);

    const editCnrNo = row.consignment_No ?? row.Consignment_No;
    setCnr(String(editCnrNo ?? ""));
    // BUGFIX: this line was missing, so doBook()'s payload sent
    // Consignment_No: null (rawConsNo's initial state) on every edit —
    // the backend then had nothing to match the row by and either
    // rejected the save or tried to insert a duplicate instead of
    // updating. rawConsNo is what actually goes on the wire as
    // Consignment_No (see doBook payload below), so it must be kept in
    // sync with the CNR being edited, same as the auto-fetch/manual-entry
    // paths already do for a brand-new booking.
    const editCnrNum = Number(editCnrNo);
    setRawConsNo(Number.isNaN(editCnrNum) ? null : editCnrNum);
    if (row.book_Dt ?? row.Book_Dt) {
      const d = new Date(row.book_Dt ?? row.Book_Dt);
      if (!Number.isNaN(d.getTime())) setBookingDate(d.toISOString().split("T")[0]);
    }

    // Pieces / weight
  const pkgs = Number(row.no_of_pkg ?? row.no_of_pkg);
    const chrgWt = Number(row.chrg_weight ?? row.chrg_weight);
    if (!Number.isNaN(pkgs)) setManualPcs(pkgs);
    if (!Number.isNaN(chrgWt)) setManualChgWt(chrgWt);

    // Rate percentages (Fuel Surcharge % / Service Tax %) and the Base Rate
    // itself. FS_Amt, ST_AMT, Taxable_Amt and Amount still aren't set here —
    // those stay fully derived (pieces × rate) so they self-correct if
    // pieces/weight are edited. Base_Amt is different: it's the ₹/kg (or
    // ₹/pkg / flat) rate that was actually applied to this booking, which
    // can legitimately differ from today's slab rate (slabs change over
    // time, or the original rate was a manual override) — so it's restored
    // here, with rateEdited forced true so calcRate() uses this saved rate
    // instead of silently re-matching against the current slab table.
    const baseAmtVal = Number(row.base_Amt ?? row.Base_Amt);
    if (!Number.isNaN(baseAmtVal)) { setBaseRate(baseAmtVal); setRateEdited(true); }
    const fuelPctVal = Number(row.fuel_Sur_Per ?? row.Fuel_Sur_Per);
    const gstPctVal = Number(row.st_Per ?? row.ST_Per);
    if (!Number.isNaN(fuelPctVal)) setFuelPct(fuelPctVal);
    if (!Number.isNaN(gstPctVal)) setGstPct(String(gstPctVal));

    // Consignor notification details
    const sms = row.sms_Number ?? row.Sms_Number;
    const wa = row.wa_Number ?? row.Wa_Number;
    const email = row.email_Address ?? row.Email_Address;
    if (sms) { setSmsNum(sms); setSmsOn(true); }
    if (wa) setWaNum(wa);
    if (email) { setEmailAddr(email); setEmailOn(true); }

    // Consignee notification details
    const cneeSms = row.cnee_Sms_Number ?? row.Cnee_Sms_Number;
    const cneeWa = row.cnee_Wa_Number ?? row.Cnee_Wa_Number;
    const cneeEmail = row.cnee_Email_Address ?? row.Cnee_Email_Address;
    if (cneeSms) setCneeSmsNum(cneeSms);
    if (cneeWa) setCneeWaNum(cneeWa);
    if (cneeEmail) setCneeEmailAddr(cneeEmail);

    // OTP settings
    // BUGFIX: this used to call setOtpRequired(...) — a leftover, unused
    // state variable with no connection to the "Enable OTP for Delivery"
    // toggle (otpOn) actually shown on screen, so an edited booking that had
    // OTP enabled loaded with the toggle showing Off and the OTP fields
    // hidden even though otpMobile/otpChannel/etc. below were populated.
    const otpReq = row.otp_Required ?? row.Otp_Required;
    if (otpReq != null) setOtpOn(!!otpReq);
    const otpMob = row.otp_Mobile ?? row.Otp_Mobile;
    if (otpMob) setOtpMobile(otpMob);
    const otpChan = row.otp_Channel ?? row.Otp_Channel;
    if (otpChan) setOtpChannel(otpChan);
    const otpVal = row.otp_Validity ?? row.Otp_Validity;
    if (otpVal != null) setOtpValidity(String(otpVal));
    const otpAtt = row.otp_Attempts ?? row.Otp_Attempts;
    if (otpAtt != null) setOtpAttempts(String(otpAtt));

    // Dimension and pod_Available are returned by the API but this form has
    // no editable field for either yet (Dimension isn't collected anywhere
    // in the booking flow, and POD availability is set later in a
    // different workflow) — they're available on `row` if you want to wire
    // up a display for them, just not mapped to any state here.

    // Commodity / declared value / special instruction / insurance / other
    // charges — these are plain user-entered fields with no auto-derivation,
    // so they're set directly from the saved booking.
    const desc = row.desc ?? row.Desc;
    if (desc) setCommodity(desc);
    const declaredVal = row.declared_value ?? row.Declared_value;
    if (declaredVal != null) setDeclVal(String(declaredVal));
    // Special_Instruction and Remarks are both saved from the same
    // specialInst field (see doBook payload), so either one reflects it —
    // prefer Special_Instruction since that's the more specific of the two.
    const specialInstVal = row.special_Instruction ?? row.Special_Instruction ?? row.remarks ?? row.Remarks;
    if (specialInstVal) setSpecialInst(specialInstVal);
    const insuranceVal = row.insurance ?? row.Insurance;
    if (insuranceVal != null) setInsAmt(insuranceVal);
    const otherChargesVal = row.other_charges ?? row.Other_charges;
    if (otherChargesVal != null) setOtherAmt(otherChargesVal);
    // Freight_Charges isn't set here — it's a derived output of the rate
    // engine (pieces × rate for the selected job/priority), not a raw input,
    // so re-injecting the old saved amount would just be overwritten by the
    // live calculation anyway, same reasoning as Base_Amt/FS_Amt/etc above.

    // Expected delivery date — set directly from the booking's own saved
    // Consnt_Delivery_Date. This runs BEFORE handlePriorityChange below,
    // because handlePriorityChange (like handlePlaceChange) recalculates
    // expDelivery from today's priority/booking-date defaults — so setting
    // it here first and letting that recalculation happen after would
    // silently discard the saved value. Setting it again below, after the
    // dropdown calls, ensures the booking's real value wins.
    const consntDeliveryDate = row.consnt_Delivery_Date ?? row.Consnt_Delivery_Date;

    // All dropdown options below carry numeric `value`s (straight from the
    // API's int/long IDs) and AutoComplete matches the selected option with
    // a strict `===`, so every id here must be passed as a Number — not a
    // string — or the field renders as if nothing were selected even
    // though the underlying id is "correct".
    const bookPlaceId = Number(row.book_Place_Id ?? row.Book_Place_Id);
    const delPlaceId = Number(row.del_Place_Id ?? row.Del_Place_Id);
    const partyId = Number(row.party_Id ?? row.Party_Id);
    const jobId = Number(row.job_id ?? row.Job_id ?? row.job_Id);
    const freightId = Number(row.freighT_TERMS_ID ?? row.FREIGHT_TERMS_ID ?? row.freight_Terms_Id);
    const priorityId = Number(row.priority_ID ?? row.Priority_ID);
    const consignorId = Number(row.consignor_Id ?? row.Consignor_Id);
    const consigneeId = Number(row.consignee_Id ?? row.Consignee_Id);

    if (!Number.isNaN(bookPlaceId)) handlePlaceChange("booking", bookPlaceId);
    if (!Number.isNaN(delPlaceId)) handlePlaceChange("delivery", delPlaceId);
    if (!Number.isNaN(freightId)) setFreight(freightId);
    if (!Number.isNaN(priorityId)) handlePriorityChange(priorityId);

    // BUGFIX: Consignor_Id / Consignee_Id come back null from get-cons-booking
    // whenever that party was typed straight into Direct Entry at booking
    // time rather than picked From List — there's no party master row to
    // resolve. Number(null) is 0, not NaN, so the old `!Number.isNaN(...)`
    // check treated a null id as "valid" and called handleCnorSel(0), which
    // silently no-ops (handleCnorSel returns early on a falsy k) and leaves
    // whichever mode was last active on screen — usually stranding the user
    // on "From List" with an empty search box even though this booking was
    // actually Direct Entry. Only route through the From-List selection
    // handler when there's a real, positive id to resolve; otherwise switch
    // straight to Direct Entry so the Name/contact/etc. fields (populated
    // below from the booking's own saved values) are the ones shown.
    const consignorFromList = !Number.isNaN(consignorId) && consignorId > 0;
    const consigneeFromList = !Number.isNaN(consigneeId) && consigneeId > 0;
    if (consignorFromList) {
      handleCnorSel(consignorId);
    } else {
      setCnorSel("");
    }
    if (consigneeFromList) {
      handleCneeSel(consigneeId);
    } else {
      setCneeSel("");
    }
    // handleCnorSel/handleCneeSel each carry their own ~50ms setTimeout that
    // auto-advances focus and forces cneeMode back to "list" (a convenience
    // for live manual entry, not appropriate when we're populating an
    // existing booking) — so this override runs after that delay to make
    // sure it's the one that sticks.
    setTimeout(() => {
      setCnorMode(consignorFromList ? "list" : "direct");
      setCneeMode(consigneeFromList ? "list" : "direct");
    }, 60);

    // BUGFIX: handlePlaceChange above re-derives Booking_Pincode/
    // Delivery_Pincode from the selected place's CURRENT master pincode
    // (same pattern as the cnorData/cneeData issue below) — so override
    // with what was actually saved on this booking. Also re-apply
    // expDelivery here since handlePriorityChange recalculates it from
    // today's date/priority defaults, which would otherwise clobber the
    // booking's real saved delivery date.
    const bookingPinVal = row.booking_Pincode ?? row.Booking_Pincode;
    if (bookingPinVal) setBookingPin(String(bookingPinVal));
    const deliveryPinVal = row.delivery_Pincode ?? row.Delivery_Pincode;
    if (deliveryPinVal) setDeliveryPin(String(deliveryPinVal));
    if (consntDeliveryDate) {
      const d = new Date(consntDeliveryDate);
      if (!Number.isNaN(d.getTime())) setExpDelivery(d.toISOString().split("T")[0]);
    }

    // BUGFIX: handleCnorSel/handleCneeSel above populate cnorData/cneeData
    // (Name, Contact Person, Mobile, Address, GSTIN, Pincode, Email — the
    // actual editable fields on the form) from allPartiesData[k], i.e.
    // today's party master record. But those fields are user-editable at
    // booking time and can legitimately differ from the master (a one-off
    // contact person, a corrected pincode, an address that's since changed
    // in the master). get-cons-booking now returns what was actually saved
    // with THIS booking (Consignor_Contact/Ph/Add/GSTN/Pincode/Email and
    // the Consignee_ equivalents) — so override cnorData/cneeData with
    // those saved values here, after selection, so edit mode shows what
    // was really booked instead of silently reverting to current master
    // data. Only applied when at least the name came back, so a bare
    // fallback to the lean search-row stub (get-cons-booking failed) still
    // leaves handleCnorSel/handleCneeSel's master-data fill in place rather
    // than blanking the fields.
    const cnorName = row.consignor_Name ?? row.Consignor_Name;
    if (cnorName) {
      setCnorData({
        name: cnorName,
        contact: row.consignor_Contact ?? row.Consignor_Contact ?? "",
        mobile: row.consignor_Ph ?? row.Consignor_Ph ?? "",
        addr: row.consignor_Add ?? row.Consignor_Add ?? "",
        gst: row.consignor_GSTN ?? row.Consignor_GSTN ?? "",
        pin: row.consignor_Pincode ?? row.Consignor_Pincode ?? "",
        email: row.consignor_Email ?? row.Consignor_Email ?? "",
      });
    }
    const cneeName = row.consignee_Name ?? row.Consignee_Name;
    if (cneeName) {
      setCneeData({
        name: cneeName,
        contact: row.consignee_Contact ?? row.Consignee_Contact ?? "",
        mobile: row.consignee_Phone ?? row.Consignee_Phone ?? "",
        addr: row.consignee_Address ?? row.Consignee_Address ?? "",
        gst: row.consignee_GSTN ?? row.Consignee_GSTN ?? "",
        pin: row.consignee_Pincode ?? row.Consignee_Pincode ?? "",
        email: row.consignee_Email ?? row.Consignee_Email ?? "",
      });
    }
    // NOTE: cnorData.mobile/email and cneeData.mobile/email changing here
    // re-triggers the effects a few lines up ("if (cnorData.mobile) {
    // setSmsNum(...) ... }") on next render, AFTER this function returns —
    // so if Consignor_Ph/consignor_Email differ from this booking's actual
    // Sms_Number/Email_Address (they're independently editable), those
    // effects can still overwrite the sms/wa/email values set above from
    // the booking snapshot. That's a pre-existing effect, not something
    // introduced here — flagging it because it's a plausible follow-up bug
    // if notification numbers ever look wrong after loading an edit.

    if (!Number.isNaN(partyId)) {
      // Await so partyJobs (and therefore jobOpts) is populated before we
      // try to select a job against it.
      await handlePartyChange(partyId);
      if (!Number.isNaN(jobId)) handleJobChange(jobId);
    }

    // BUGFIX: handlePartyChange unconditionally does setRateEdited(false)
    // (and, when the party isn't in the static PARTIES fallback map — true
    // for every real party id from the API — leaves baseRate untouched at
    // whatever it was before). Since handlePartyChange is awaited above,
    // AFTER the Base_Amt restore earlier in this function, its
    // setRateEdited(false) runs later and silently undoes that restore,
    // making calcRate() fall back to re-matching today's slab rate instead
    // of the rate actually saved on this booking. Re-apply it here so it's
    // the one that sticks.
    if (!Number.isNaN(baseAmtVal)) { setBaseRate(baseAmtVal); setRateEdited(true); }

    setStep(1);
    setView("form");
    addToast("Booking loaded — review and update the form below.", "ok");
  };

  /* ── SEARCH WINDOW: delete a row ── */
  const confirmSearchDelete = async () => {
    if (!searchDeleteRow) return;
    const id = searchDeleteRow.consignment_Id ?? searchDeleteRow.Consignment_Id;
    setSearchDeleting(true);
    try {
      const res = await api.delete(`/delete-cons-booking/${id}`);
      if (res.data?.success) {
        addToast(res.data.message || "Booking deleted.", "ok");
        setSearchRows(prev => prev.filter(r => (r.consignment_Id ?? r.Consignment_Id) !== id));
        setSearchDeleteRow(null);
      } else {
        addToast(res.data?.message || "Delete failed.", "err");
      }
    } catch (err) {
      addToast(err.response?.data?.message || "This booking could not be deleted.", "err");
    } finally {
      setSearchDeleting(false);
    }
  };

  const fetchNotificationEvents = async () => {
    try {
      const res = await api.get('/get-standard-notifications');
      // API returns { success, count, data: [...] }
      const arr = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setNotificationEvents(arr);
    } catch (err) { console.error("Failed to fetch notifications:", err); }
  };

  const fetchFreightTerms = async () => {
    try {
      const res = await api.get('/get-freight-terms');
      setFreightTermOpts(res.data);
    } catch (err) { console.error("Failed to fetch freight terms:", err); }
  };

  const fetchPriorities = async () => {
    try {
      const res = await api.get('/get-priorities');
      setPriorityOpts(res.data);
    } catch (err) { console.error("Failed to fetch priorities:", err); }
  };

  // fetchEvents removed — notificationEvents already contains channelId, channelName, eventId, eventName

  const fetchAllParties = async () => {
    try {
      const res = await api.get('/get-parties');
      const data = res.data;
      const mapped = data.map(p => ({
        value: p.party_id || p.Party_id,
        label: p.party_Name || p.Party_Name
      }));
      setCnorOpts(mapped);
      setCneeOpts(mapped);
      const lookup = {};
      data.forEach(p => {
        const id = p.party_id || p.Party_id;
        lookup[id] = {
          name: p.party_Name || p.Party_Name || "",
          contact: p.contact_Person || p.Contact_Person || "",
          mobile: p.mobile_No || p.Mobile_No || "",
          addr: p.address || "",
          gst: p.gstin || "",
          pin: p.pincode || p.Pincode || "",
          notifEmail: (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 3) || (p.notification_Preference || p.Notification_Preference) === "E",
          notifSms:   (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 1) || (p.notification_Preference || p.Notification_Preference) === "S",
          notifWa:    (p.notifications || p.Notifications || []).some(n => (n.event_id || n.Event_id) === 2) || (p.notification_Preference || p.Notification_Preference) === "W",
          smsNumber:    p.sms_Number   || p.Sms_Number   || p.mobile_No || p.Mobile_No || "",
          emailAddress: p.party_Email  || p.Party_Email  || p.email_Address || p.Email_Address || "",
          waNumber:     p.wa_Number    || p.Wa_Number    || p.mobile_No || p.Mobile_No || "",
          selectedEvents: (p.notifications || p.Notifications || []).map(n =>
            n.notification_event_id || n.Notification_Event_id || n.Notification_event_id || n.notification_Event_id
          )
        };
      });
      setAllPartiesData(lookup);
    } catch (err) { console.error("Failed to fetch all parties:", err); }
  };

  /* ══════════════════════════════════════════════════
     FETCH RATE DETAILS (from Rate master)
  ══════════════════════════════════════════════════ */
  const fetchRateDetails = useCallback(async (partyId, bookPlaceId, delPlaceId) => {
    if (!partyId || !bookPlaceId || !delPlaceId) {
      setFetchedRateHeader(null);
      setFetchedRateDetails([]);
      return;
    }
    setRateLoading(true);
    try {
      const res = await api.get('/get-rate-details', {
        params: {
          partyId: parseInt(partyId),
          bookPlaceId: parseInt(bookPlaceId),
          delPlaceId: parseInt(delPlaceId)
        }
      });
      const { rateHeader, rateDetails } = res.data;
      setFetchedRateHeader(rateHeader || null);
      setFetchedRateDetails(rateDetails || []);
      if (rateHeader) {
        addToast(`Rate card loaded — ${rateDetails?.length || 0} slab(s)`, "ok");
      }
    } catch (err) {
      console.error("Failed to fetch rate details:", err);
      setFetchedRateHeader(null);
      setFetchedRateDetails([]);
    } finally {
      setRateLoading(false);
    }
  }, []);

  // Auto-fetch rate details when party, booking place, or delivery place changes
  useEffect(() => {
    if (partyKey && bookingPlace && deliveryPlace) {
      fetchRateDetails(partyKey, bookingPlace, deliveryPlace);
    } else {
      setFetchedRateHeader(null);
      setFetchedRateDetails([]);
    }
  }, [partyKey, bookingPlace, deliveryPlace, fetchRateDetails]);

  /**
   * Find the matching rate amount from fetched rate slabs based on weight.
   * Looks for the slab where From_Wgt <= weight <= To_Wgt.
   * Returns { rateAmount, additionalWtRate, uomCode } or null if no match.
   */
  function getSlabRate(weight) {
    if (!fetchedRateDetails || fetchedRateDetails.length === 0) return null;
    const w = parseFloat(weight) || 0;
    // Find matching slab
    const slab = fetchedRateDetails.find(d => {
      const from = parseFloat(d.from_Wgt ?? d.From_Wgt) || 0;
      const to = parseFloat(d.to_Wgt ?? d.To_Wgt) || 0;
      return w >= from && w <= to;
    });
    if (slab) {
      return {
        rateAmount: parseFloat(slab.rateAmount ?? slab.RateAmount) || 0,
        additionalWtRate: parseFloat(slab.addl_Wgt ?? slab.Addl_Wgt) || 0,
        uomCode: slab.uoM_Code ?? slab.UOM_Code ?? "",
        fromWgt: parseFloat(slab.from_Wgt ?? slab.From_Wgt) || 0,
        toWgt: parseFloat(slab.to_Wgt ?? slab.To_Wgt) || 0
      };
    }
    // If weight exceeds all slabs, use the last (highest) slab + additional weight calculation
    const lastSlab = fetchedRateDetails[fetchedRateDetails.length - 1];
    if (lastSlab) {
      const lastTo = parseFloat(lastSlab.to_Wgt ?? lastSlab.To_Wgt) || 0;
      const lastRate = parseFloat(lastSlab.rateAmount ?? lastSlab.RateAmount) || 0;
      const addlRate = parseFloat(lastSlab.addl_Wgt ?? lastSlab.Addl_Wgt) || 0;
      if (w > lastTo && addlRate > 0) {
        const extraWt = w - lastTo;
        return {
          rateAmount: lastRate + (extraWt * addlRate),
          additionalWtRate: addlRate,
          uomCode: lastSlab.uoM_Code ?? lastSlab.UOM_Code ?? "",
          fromWgt: lastTo,
          toWgt: w
        };
      }
    }
    return null;
  }

  /* ══════════════════════════════════════════════════
     ROUTE HANDLERS
  ══════════════════════════════════════════════════ */
  function handlePlaceChange(type, val) {
    if (type === "booking") {
      setBookingPlace(val);
      const opt = bookingPlaceOpts.find(o => String(o.value) === String(val));
      if (opt) {
        setBookingPin(opt.pincode || "");
        setBpinInfo(opt.pincode ? `📍 ${opt.pincode} — ${opt.label}` : `📍 ${opt.label}`);
      } else {
        const pin = PINCODES[val] || "";
        setBookingPin(pin);
        setBpinInfo(pin ? `📍 ${pin} — ${PLACE_NAMES[val] || val}` : "");
      }
    } else {
      setDeliveryPlace(val);
      const opt = deliveryPlaceOpts.find(o => String(o.value) === String(val));
      if (opt) {
        setDeliveryPin(opt.pincode || "");
        setDpinInfo(opt.pincode ? `📍 ${opt.pincode} — ${opt.label}` : `📍 ${opt.label}`);
      } else {
        const pin = PINCODES[val] || "";
        setDeliveryPin(pin);
        setDpinInfo(pin ? `📍 ${pin} — ${PLACE_NAMES[val] || val}` : "");
      }
    }
    setExpDelivery(calcExpDeliveryDate(priority, bookingDate));
  }

  function handlePriorityChange(val) {
    setPriority(val);
    const id = parseInt(val);
    const priorityKey = id === 2 ? "express" : id === 3 ? "urgent" : "standard";
    setExpDelivery(calcExpDeliveryDate(priorityKey, bookingDate));
  }

  function handleDateChange(val) {
    setBookingDate(val);
    const id = parseInt(priority);
    const priorityKey = id === 2 ? "express" : id === 3 ? "urgent" : "standard";
    setExpDelivery(calcExpDeliveryDate(priorityKey, val));
  }

  /* ══════════════════════════════════════════════════
     PARTY HANDLER
  ══════════════════════════════════════════════════ */
  async function handlePartyChange(k) {
    setPartyKey(k);
    setSelectedJob(null);
    setPartyJobs([]);
    setJobDivisors({ chrgwtAir: null, chrgwtSur: null }); // new party — old job's divisors no longer apply
    setRateEdited(false); // new party context — re-offer auto/slab rate instead of keeping a stale manual override
    if (!k) { setPartyGst(""); setBaseRate(0); return; }
    const p = PARTIES[k];
    if (p) { setPartyGst(p.gst || ""); setBaseRate(p.rate || 0); setRateBasis(p.basis || "perkg"); }
    try {
      const gstRes = await api.get(`/get-party-gstin/${k}`);
      if (gstRes.data && (gstRes.data.success || gstRes.data.GSTIN || gstRes.data.gstin)) {
        setPartyGst(gstRes.data.GSTIN || gstRes.data.gstin || "");
      }
      const jobsRes = await api.get(`/get-job-list/${k}`);
      if (jobsRes.data) setPartyJobs(jobsRes.data);
      syncPartyNotifications(k);
    } catch (err) { console.error("Failed to fetch party details:", err); }
  }

  // GET /get-job-charged-weights/{job_id} — pulls the Air/Surface volumetric
  // divisor for the chosen Job from JOB_Master (chrgwtAir / chrgwtSur),
  // replacing the old hardcoded ÷5000 constant. Both come back null if the
  // job has nothing configured, which the rest of the form treats as
  // "can't calculate volumetric weight yet".
  async function fetchJobDivisors(jobId) {
    if (!jobId) { setJobDivisors({ chrgwtAir: null, chrgwtSur: null }); return; }
    setJobDivisorsLoading(true);
    try {
      const res = await api.get(`/get-job-charged-weights/${jobId}`);
      if (res.data && res.data.success) {
        setJobDivisors({
          chrgwtAir: parseFloat(res.data.chrgwtAir) || null,
          chrgwtSur: parseFloat(res.data.chrgwtSur) || null,
        });
      } else {
        setJobDivisors({ chrgwtAir: null, chrgwtSur: null });
      }
    } catch (e) {
      console.error("Failed to fetch job charged-weight divisors:", e);
      setJobDivisors({ chrgwtAir: null, chrgwtSur: null });
    } finally {
      setJobDivisorsLoading(false);
    }
  }

  async function fetchJobOtpRequired(jobId) {
    if (!jobId) { setJobOtpMandatory(false); return; }
    try {
      const res = await api.get(`/get-job-otp-required/${jobId}`);
      const mandatory = !!(res.data && res.data.success && res.data.otpRequired);
      setJobOtpMandatory(mandatory);
      if (mandatory) setOtpOn(true); // job mandates OTP — switch it on automatically
    } catch (e) {
      console.error("Failed to fetch job OTP requirement:", e);
      setJobOtpMandatory(false);
    }
  }

  function handleJobChange(v) {
    setSelectedJob(v || null);
    setShipMode(""); // force a fresh Air/Surface pick against the new job's divisors
    fetchJobDivisors(v || null);
    fetchJobOtpRequired(v || null);
  }

  /* ══════════════════════════════════════════════════
     CONSIGNOR / CONSIGNEE
  ══════════════════════════════════════════════════ */
  function handleCnorSel(k) {
    setCnorSel(k);
    if (!k) return;
    const d = allPartiesData[k];
    if (d) {
      setCnorData({ name: d.name, contact: d.contact, mobile: d.mobile, addr: d.addr, gst: d.gst, pin: d.pin });
      syncPartyNotifications(k);
    }
    // Stay on "From List" mode — jump focus straight to Consignee search
    setTimeout(() => {
      const cneeInput = document.getElementById("cb-cnee-ac-input");
      if (cneeInput) { cneeInput.focus(); setCneeMode("list"); }
    }, 50);
  }

  // Small reusable keydown handler: Enter or forward-Tab jumps straight to
  // the field with id `nextId`, instead of following native tab order or
  // letting the page's global Enter handler pick whatever field happens to
  // sit next in the DOM. stopPropagation is required so that global handler
  // doesn't also fire on the bubble and re-focus the wrong field.
  function makeFieldAdvance(nextId) {
    return e => {
      if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
        e.preventDefault();
        e.stopPropagation();
        document.getElementById(nextId)?.focus();
      }
    };
  }

  function handleCneeSel(k) {
    setCneeSel(k);
    if (!k) return;
    const d = allPartiesData[k];
    if (d) {
      setCneeData({ name: d.name, contact: d.contact, mobile: d.mobile, addr: d.addr, gst: d.gst, pin: d.pin });
      if (d.mobile) setOtpMobile(d.mobile);
    }
    // Stay on "From List" mode — show the populated Name field first so the
    // user can see/confirm who they picked; only move on to Next when they
    // press Enter from there.
    setTimeout(() => {
      const nameInput = document.getElementById("cb-cnee-name-input");
      if (nameInput) nameInput.focus();
    }, 50);
  }

  function handleToggleOTP(checked) {
    setOtpOn(checked);
    if (checked && cneeData.mobile) setOtpMobile(cneeData.mobile);
  }

  /* ══════════════════════════════════════════════════
     VOLUMETRIC DIVISOR (Air / Surface)
     Replaces the old hardcoded ÷5000 — the real divisor comes from
     JOB_Master.chrgwtAir / chrgwtSur for whichever radio is selected.
  ══════════════════════════════════════════════════ */
  const volDivisor = shipMode === "air" ? jobDivisors.chrgwtAir
                    : shipMode === "surface" ? jobDivisors.chrgwtSur
                    : null;
  // Volumetric calc is only "ready" once a mode is picked AND that mode's
  // divisor actually came back from the backend as a usable (>0) number.
  const volDivisorReady = !!shipMode && !!volDivisor && volDivisor > 0;

  /* ══════════════════════════════════════════════════
     PIECES
  ══════════════════════════════════════════════════ */
  function addPiece() {
    if (isVolumetric && !volDivisorReady) {
      addToast(
        !shipMode
          ? "Please select Air or Surface before adding pieces"
          : jobDivisorsLoading
            ? "Still fetching the charged-weight value — please wait"
            : `No ${shipMode === "air" ? "Air" : "Surface"} charged-weight value is set up for this Job — cannot add pieces`,
        "warn"
      );
      return;
    }
    setPieces(p => [...p, { id: pidCtr, desc: "", pkgType: "Box / Carton", qty: 1, l: 0, w: 0, h: 0, actW: 0 }]);
    setPidCtr(c => c + 1);
  }

  /* ══════════════════════════════════════════════════
     TOTALS & RATE
  ══════════════════════════════════════════════════ */
  function getTotals() {
    let tQ = 0, tA = 0, tV = 0, tC = 0;
       if (!isVolumetric) {
      tQ = parseInt(manualPcs) || 0;
      tC = parseFloat(manualChgWt) || 0;
      return { tQ, tA, tV, tC };
    }
    // No usable divisor yet (mode not picked, or backend has no value for
    // it) — don't fabricate a volumetric figure from a wrong/missing constant.
    if (!volDivisorReady) return { tQ, tA, tV, tC };
    pieces.forEach(({ qty, l, w, h, actW }) => {
      const vt = ((l * w * h) / volDivisor) * qty;
      const at = actW * qty;
      tQ += qty; tA += at; tV += vt; tC += Math.max(at, vt);
    });
    return { tQ, tA, tV, tC };
  }

  function calcRate() {
    const { tQ, tC } = getTotals();
    const fp = parseFloat(fuelPct) || 0;
    const gp = parseFloat(gstPct) || 5;
    const ia = parseFloat(insAmt) || 0;
    const oa = parseFloat(otherAmt) || 0;

    // Try to get rate from API slab based on charged weight
    const slabMatch = getSlabRate(tC);
    let effectiveRate;
    if (slabMatch && !rateEdited) {
      // Use rate amount directly from the matched slab — unless the user
      // has manually overridden it, in which case their typed value wins.
      effectiveRate = slabMatch.rateAmount;
    } else {
      // Manual base rate — either no slab matched, or user overrode the slab rate.
      effectiveRate = parseFloat(baseRate) || 0;
    }

    const freight_ = rateBasis === "perkg" ? effectiveRate * tC
                   : rateBasis === "perpkg" ? effectiveRate * tQ
                   : effectiveRate;
    const fuel = freight_ * fp / 100;
    const sub = freight_ + fuel + ia + oa;
    const gst = sub * gp / 100;
    return {
      freight: freight_, fuel, ia, oa, sub, gst, total: sub + gst,
      effectiveRate, slabMatch
    };
  }

  const rate = calcRate();
  const { tQ, tA, tV, tC } = getTotals();
  const totals = getTotals();

  /* ══════════════════════════════════════════════════
     IMAGES
  ══════════════════════════════════════════════════ */
  function handleFiles(files) {
    Array.from(files).forEach((f) => {
      if (images.length >= 20) { addToast("Max 20 files allowed", "warn"); return; }
      if (f.size > 1 * 1024 * 1024) { addToast(f.name + " exceeds 1 MB limit", "err"); return; }
      const id = Date.now() + Math.random();
      const r = new FileReader();
      // store raw file object alongside preview — needed for FormData upload
      r.onload = (ev) => setImages(imgs => [...imgs, { id, name: f.name, type: imgType, src: ev.target.result, isImg: f.type.startsWith("image"), file: f }]);
      r.readAsDataURL(f);
    });
  }

  /* ══════════════════════════════════════════════════
     VALIDATION (checked one-by-one, stops at first failure)
     Each failing check returns { step, fieldId, message } instead of a
     plain string, so the caller knows exactly which field to jump the
     user to (and which step that field lives on).
  ══════════════════════════════════════════════════ */
  function err(stepNum, fieldId, message, action) {
    return { step: stepNum, fieldId, message, action };
  }

  // ── STEP 1: Route & Party ──
  function validateStep1() {
    if (!cnr.trim()) return err(1, "cb-cnr-input", "Please select or enter a Consignment Number");
    // Skip the duplicate-CNR check while editing an existing booking — this
    // Consignment No is expected to already exist in the DB (it's the row
    // being updated), so the "already booked" gate only applies to a
    // brand-new booking.
    if (!editingConsignmentId && cnrDuplicate) return err(1, "cb-cnr-input", "This Consignment No is already booked. Please use a different number.");
    if (!bookingPlace) return err(1, "cb-booking-place-input", "Please select a Booking Place");
    // Party is required on Desktop, but NOT on Mobile Version — Mobile still
    // shows the Party field (for the Job dropdown to filter by), but the
    // user shouldn't be blocked from submitting if it's left empty there.
    if (!isMobile) {
      if (!partyKey) return err(1, "cb-party-input", "Please select a Party");
      if (!selectedJob) return err(1, "cb-job-input", "Please select a Job / Department");
    } else {
      // On Mobile, Job/Department only has options once a Party is picked
      // (jobOpts is empty without one) — so only require Job if the user
      // actually did pick a Party. If Party was left blank, skip Job too.
      if (partyKey && !selectedJob) return err(1, "cb-job-input", "Please select a Job / Department");
    }
    // Booking/Delivery Pincode, Freight Terms and Priority are not shown on
    // the Mobile Version screen (they're auto-filled/auto-defaulted instead —
    // see handlePlaceChange and the isMobile effects above). Only enforce
    // these checks on the Full Version, where the fields actually exist for
    // the user to fix.
    if (!isMobile) {
      if (!bookingPin.trim()) return err(1, "cb-booking-pin-input", "Please enter a Booking Pincode");
      if (!deliveryPlace) return err(1, "cb-delivery-place-input", "Please select a Delivery Place");
      if (!deliveryPin.trim()) return err(1, "cb-delivery-pin-input", "Please enter a Destination Pincode");
      if (!freight) return err(1, "cb-freight-input", "Please select Freight Terms");
      if (!priority) return err(1, "cb-priority-input", "Please select a Priority");
    } else {
      // Mobile still needs a Delivery Place (it's a visible required field there) —
      // just skip the pincode/freight/priority checks that have no on-screen field.
      if (!deliveryPlace) return err(1, "cb-delivery-place-input", "Please select a Delivery Place");
    }
    return null;
  }

  // ── STEP 2: Consignor / Consignee ──
  function validateStep2() {
    // Mobile Version never shows the "From List vs Direct Entry" toggle at
    // all — MobileFlatForm only has a Name field for both Consignor and
    // Consignee. cnorMode/cneeMode default to "list" and only flip to
    // "direct" via the isMobile effect on mount, so relying on that state
    // here means this check could briefly run against the wrong mode. On
    // mobile, skip cnorMode/cneeMode entirely and just validate the Name
    // fields that are actually on screen.
    if (isMobile) {
      if (!cnorData.name || !cnorData.name.trim()) return err(2, "cb-cnor-name-input", "Please enter Consignor Name");
      if (!cneeData.name || !cneeData.name.trim()) return err(2, "cb-cnee-name-input", "Please enter Consignee Name");
      return null;
    }

    // In "From List" mode we need a party picked from the dropdown; in
    // "Direct Entry" mode there's no list selection at all, so instead we
    // just require the Name field (the only mandatory direct-entry field)
    // to be filled in.
    if (cnorMode === "list") {
      if (!cnorSel) return err(2, "cb-cnor-ac-input", "Please select a Consignor");
    } else if (!cnorData.name || !cnorData.name.trim()) {
      return err(2, "cb-cnor-name-input", "Please enter Consignor Name");
    }
    if (cneeMode === "list") {
      if (!cneeSel) return err(2, "cb-cnee-ac-input", "Please select a Consignee");
    } else if (!cneeData.name || !cneeData.name.trim()) {
      return err(2, "cb-cnee-name-input", "Please enter Consignee Name");
    }

    // OTP Delivery Confirmation only appears on the Full/Desktop version —
    // Mobile Version has no OTP toggle at all, so otpOn is always false
    // there and this check simply never triggers on that screen.
    if (otpOn) {
      if (!otpMobile || !otpMobile.trim()) {
        return err(2, "cb-otp-mobile-input", "Please enter Consignee OTP Mobile");
      }
      if (otpMobile.trim().length !== 10) {
        return err(2, "cb-otp-mobile-input", "Consignee OTP Mobile must be a valid 10-digit number");
      }
    }

    return null;
  }

  // Validates every step strictly BEFORE targetStep (e.g. targetStep=3 checks
  // step 1 & 2). Returns the first error found, or null if all clear — used
  // to gate forward navigation so a tab can't be skipped with missing fields.
  function validateUpTo(targetStep) {
    if (targetStep > 1) {
      const e1 = validateStep1();
      if (e1) return e1;
    }
    if (targetStep > 2) {
      const e2 = validateStep2();
      if (e2) return e2;
    }
    return null;
  }

  // Scrolls to + focuses the field named by a validation error, highlighting
  // it briefly so it's obvious at a glance which field needs attention. If
  // the field lives on a step the user isn't currently viewing, switches to
  // that step first and waits for it to render before focusing.
  function focusFieldWithHighlight(fieldId) {
    if (!fieldId) return;
    try {
      const el = document.getElementById(fieldId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });

      const prevOutline = el.style.outline;
      const prevOutlineOffset = el.style.outlineOffset;
      const prevBoxShadow = el.style.boxShadow;
      el.style.outline = "2px solid #E53E3E";
      el.style.outlineOffset = "2px";
      el.style.boxShadow = "0 0 0 4px rgba(229,62,62,0.15)";

      let cleared = false;
      const clear = () => {
        if (cleared) return;
        cleared = true;
        el.style.outline = prevOutline;
        el.style.outlineOffset = prevOutlineOffset;
        el.style.boxShadow = prevBoxShadow;
        el.removeEventListener("input", clear);
        el.removeEventListener("change", clear);
        el.removeEventListener("blur", clear);
      };
      el.addEventListener("input", clear);
      el.addEventListener("change", clear);
      el.addEventListener("blur", clear);
      setTimeout(clear, 4000); // safety net in case the user fixes it via mouse-only selection

      setTimeout(() => {
        try { el.focus({ preventScroll: true }); } catch (e2) { /* ignore */ }
      }, 300);
    } catch (e) {
      // ignore — highlighting is a nice-to-have, never break validation flow
    }
  }

  // Switches to the step a validation error belongs to (if not already
  // there) and focuses/highlights the offending field once that step has
  // rendered, then surfaces the message as a toast. Some errors also carry
  // an `action` (e.g. "addPiece") that fixes the gap automatically instead
  // of just pointing at empty space — run that first so the field being
  // focused actually exists in the DOM by the time we try.
  function reportFieldError(error) {
    if (!error) return;
    addToast(error.message, "warn");

    const runActionThenFocus = () => {
      if (error.action === "addPiece") addPiece();
      // Wait a tick for React to render the new row before grabbing it by id.
      setTimeout(() => focusFieldWithHighlight(error.fieldId), 60);
    };

    if (error.step && error.step !== step) {
      setStep(error.step);
      setTimeout(runActionThenFocus, 60);
    } else {
      runActionThenFocus();
    }
  }

  // Attempts to move to `target` step. Moving backward is always allowed;
  // moving forward is blocked (with a toast + field focus) until every
  // earlier step passes its own validation.
  function goToStep(target) {
    // Freeze all step navigation while a modal (Confirm & Book, Success,
    // Sticker, or the post-booking notification prompt) is on screen —
    // only the modal itself should be interactable at that point.
    if (confirmOpen || successOpen || stickerOpen || notifModal.open) return;
    if (target <= step) {
      setStep(target);
      return;
    }
    const error = validateUpTo(target);
    if (error) {
      reportFieldError(error);
      return;
    }
    setStep(target);
  }

  function validateForm() {
    const stepError = validateUpTo(3);
    if (stepError) return stepError;

// Re-compute slab independently of render-time 'rate' object
// because tC must be > 0 for slab to match
const { tC: validationWeight } = getTotals();
const validationSlab = getSlabRate(validationWeight);
const usingManualRate = !validationSlab || rateEdited;

// The Base Rate field (and the whole rate-review section) isn't rendered on
// the Mobile Version screen — baseRate there only ever gets a value as a
// side effect of picking a Party (see handlePartyChange's `setBaseRate(p.rate || 0)`).
// So on mobile we can't ask the user to "please enter a Rate" — there's
// nowhere on screen for them to do it — and blocking submission on a field
// they can't see just gets them stuck. We only run this stricter check on
// the Full Version, where the Rate field is actually visible/editable.
if (!isMobile) {
  if (usingManualRate) {
    if (baseRate === "" || baseRate === null || baseRate === undefined) {
      return err(3, "cb-base-rate-input", "Please enter a Rate");
    }
    const effectiveRateNum = parseFloat(baseRate);
    if (isNaN(effectiveRateNum) || effectiveRateNum <= 0) {
      return err(3, "cb-base-rate-input", "Rate must be greater than 0");
    }
  } else {
    // Slab auto-matched — guard against slab having 0 rate
    const slabRateNum = parseFloat(validationSlab.rateAmount);
    if (isNaN(slabRateNum) || slabRateNum <= 0) {
      return err(3, "cb-base-rate-input", "Auto-matched slab rate is 0 — please check the rate master");
    }
  }
}
    if (!isVolumetric) {
      // Non-volumetric mode: top-level pieces/weight are required and must be > 0
      if (!tQ || tQ <= 0) return err(3, "cb-manual-pcs-input", "Please enter No. of Pieces (greater than 0)");
      if (!tC || tC <= 0) return err(3, "cb-chg-wt-input", "Please enter Charged Weight (greater than 0)");
    } else {
      // Volumetric mode: an Air/Surface divisor must have been picked AND
      // actually resolved to a usable value before anything else is checked —
      // there's no point validating piece rows against a divisor that isn't there.
      if (!shipMode) return err(3, "cb-shipmode-row", "Please select Air or Surface for Volumetric Weight");
      if (!volDivisorReady) {
        return err(3, null, `No ${shipMode === "air" ? "Air" : "Surface"} charged-weight value is configured for this Job — cannot calculate Volumetric Weight`);
      }
      // Volumetric mode: need at least one piece row, and each row fully filled in.
      // fieldId targets row 0's Description input, with an "addPiece" action so
      // reportFieldError adds that first row for the user instead of leaving
      // them to click "+ Add Piece" themselves after reading the toast.
      if (!pieces || pieces.length === 0) return err(3, "cb-piece-0-desc", "Please add at least one piece row", "addPiece");
      for (let i = 0; i < pieces.length; i++) {
        const p = pieces[i];
        const rowLabel = `Piece row ${i + 1}`;
        if (!p.desc || !p.desc.trim()) return err(3, `cb-piece-${i}-desc`, `${rowLabel}: Description is required`);
        if (!p.pkgType || !p.pkgType.trim()) return err(3, `cb-piece-${i}-pkgType`, `${rowLabel}: Package Type is required`);
        if (!p.qty || p.qty <= 0) return err(3, `cb-piece-${i}-qty`, `${rowLabel}: Qty must be greater than 0`);
        if (!p.actW || p.actW <= 0) return err(3, `cb-piece-${i}-actW`, `${rowLabel}: Actual Weight must be greater than 0`);
      }
    }

    // Same reasoning as the Base Rate skip above: on mobile there's no Rate
    // field, so if the amount comes out to 0 there's nothing on screen for
    // the user to go fix — blocking here would just leave them stuck same
    // as before. Only enforce this amount guard on the Full Version.
    if (!isMobile) {
      if (!rate.sub || rate.sub <= 0) return err(3, null, "Taxable Amount could not be calculated — check rate and weight");
      if (!rate.total || rate.total <= 0) return err(3, null, "Total Amount could not be calculated — check rate and weight");
    }

    return null; // all good
  }

  /* ══════════════════════════════════════════════════
     CONFIRM & BOOK (API save)
  ══════════════════════════════════════════════════ */
  function openConfirm() {
    const error = validateForm();
    if (error) {
      reportFieldError(error);
      return;
    }
    // Scroll to top first so the fixed overlay/modal is never clipped
    // or misaligned when the page was scrolled down before clicking Confirm & Book
    scrollPageToTop();
    setConfirmOpen(true);
  }

  async function doBook() {
    if (isBooking) return;
    if (!cnr.trim()) { reportFieldError(err(1, "cb-cnr-input", "Please select a Consignment Number")); return; }
    setIsBooking(true);
    addToast("Processing booking...", "info");
    try {
      const payload = {
        Party_Id: parseInt(partyKey) || null,
        Ref_No: "",
        Consignor_Name: cnorData.name,
        Consignee_Name: cneeData.name,
        // Book_Place: bookingPlaceOpts.find(o => String(o.value) === String(bookingPlace))?.label || bookingPlace,
        // Delivery_Place: deliveryPlaceOpts.find(o => String(o.value) === String(deliveryPlace))?.label || deliveryPlace,
        Book_Place_Id: parseInt(bookingPlace) || null,
Del_Place_Id: parseInt(deliveryPlace) || null,
        Weight: parseFloat(tC) || 0,
        No_Pkgs: parseInt(tQ) || 0,
        Company_Code: 1,
        FinYear_Code: 1,
        Created_By: 1,
        Booked: "Y",
        Consignment_No: rawConsNo,
        // Editing an existing booking (loaded via editSearchRow)? Send its
        // Consignment_Id so the backend UPDATEs that row — including
        // Book_Time, refreshed to "now" just below — instead of inserting a
        // new one. Left as 0 for a brand-new booking, which the backend
        // treats as INSERT.
        Consignment_Id: editingConsignmentId || 0,
        Is_Manual_Entry: cnrEdited,   // true => user typed the CNR, skip issued-table check; backend checks ConsBookings instead
        // Tells the backend this came from the Mobile Version screen, where
        // Pincode / Freight Terms / Priority / Rate fields don't exist on
        // screen for the user to fill in or fix. The controller uses this
        // to skip those specific checks instead of hard-rejecting bookings
        // that have nothing on screen the user could correct.
        Is_Mobile_Booking: isMobile,
        // ── Notification gating ──────────────────────────────────────────
        // Same logic as the post-booking modal's hasEmail/hasWa check: the
        // channel toggle must be ON AND the BOOKING event must be checked
        // for that channel. Backend uses these to decide whether to fire
        // SMS/WhatsApp after save — it no longer sends unconditionally.
        Send_Sms_Notification: (() => {
          // Mobile Version has no Notifications panel at all (no smsOn
          // toggle, no event checkboxes) — so gating on them here would
          // mean SMS NEVER fires on mobile, no matter what the user enters.
          // On mobile, send directly whenever a Consignor mobile number was
          // typed into the form, no toggle/event check needed. Desktop
          // keeps the original toggle + booking-event gating exactly as
          // before.
          if (isMobile) return Boolean(cnorData.mobile);
          const bookingEvents = notificationEvents.filter(isBookingEvent);
          return smsOn && bookingEvents.some(e => e.channelId === 1 && matchesRecipient(e, "_CR") && selectedEvents.includes(e.eventId));
        })(),
        Send_Wa_Notification: (() => {
          // Mobile Version has no Notifications panel at all (no waOn toggle,
          // no event checkboxes) — so gating on them here would mean
          // WhatsApp NEVER fires on mobile, no matter what the user enters.
          // On mobile, send directly whenever a Consignor mobile number was
          // typed into the form, no toggle/event check needed. Desktop
          // keeps the original toggle + booking-event gating exactly
          // as before.
          if (isMobile) return Boolean(cnorData.mobile);
          const bookingEvents = notificationEvents.filter(isBookingEvent);
          return waOn && bookingEvents.some(e => e.channelId === 2 && matchesRecipient(e, "_CR") && selectedEvents.includes(e.eventId));
        })(),
        Send_Email_Notification: (() => {
          const bookingEvents = notificationEvents.filter(isBookingEvent);
          return emailOn && bookingEvents.some(e => e.channelId === 3 && matchesRecipient(e, "_CR") && selectedEvents.includes(e.eventId));
        })(),
        // ── Consignee-specific notification gating ───────────────────────
        // The panel above is shared, but its Mobile No./Email fields only
        // reliably reflect ONE recipient at a time. So whenever a checked
        // Booking event is actually a Consignee event ("_CE" suffix — see
        // matchesRecipient/isBookingEvent above), we fire a SEPARATE send
        // straight to the Consignee's OWN contact info (Consignee_Phone /
        // consignee_Email below), instead of trusting whatever's currently
        // sitting in the shared smsNum/waNum/emailAddr fields. This does
        // NOT change the existing Send_*_Notification flags above — those
        // keep working exactly as before for Consignor-facing events.
        Send_Sms_Notification_Consignee: (() => {
          // Same Mobile-only bypass as Send_Sms_Notification above, but for
          // the Consignee's own mobile number — send directly whenever it
          // was typed in, no toggle/event check needed.
          if (isMobile) return Boolean(cneeData.mobile);
          const bookingEvents = notificationEvents.filter(isBookingEvent);
          return smsOn && bookingEvents.some(e => e.channelId === 1 && matchesRecipient(e, "_CE") && selectedEvents.includes(e.eventId));
        })(),
        Send_Wa_Notification_Consignee: (() => {
          // Same Mobile-only bypass as Send_Wa_Notification above, but for
          // the Consignee's own mobile number (cneeData.mobile).
          if (isMobile) return Boolean(cneeData.mobile);
          const bookingEvents = notificationEvents.filter(isBookingEvent);
          return waOn && bookingEvents.some(e => e.channelId === 2 && matchesRecipient(e, "_CE") && selectedEvents.includes(e.eventId));
        })(),
        Send_Email_Notification_Consignee: (() => {
          const bookingEvents = notificationEvents.filter(isBookingEvent);
          return emailOn && bookingEvents.some(e => e.channelId === 3 && matchesRecipient(e, "_CE") && selectedEvents.includes(e.eventId));
        })(),
        Book_Dt: bookingDate,
        // bookingTime is captured once at mount (useState initializer) and
        // never updates again — fine for the read-only Time field the user
        // sees, but wrong for what actually gets saved: if the screen stays
        // open and the user books several consignments in one session,
        // every one of them would get the exact same Book_Time (the moment
        // the page first loaded) instead of the moment each was actually
        // saved. So Book_Time is built here from a fresh `new Date()` taken
        // right at submit, in the same "YYYY-MM-DD HH:MM:SS" shape as
        // CVBookingForm.cshtml's currentDateTime — bookingDate is left as-is
        // since that field IS user-editable and should reflect whatever the
        // user has set/left in the Date field.
        //
        // Uses "T" as the date/time separator, not a space. cshtml's own
        // currentDateTime uses a space, but that value there just sits in a
        // form field's value attribute as plain text — never JSON-deserialized
        // into a real DateTime. Here it IS deserialized server-side, and
        // System.Text.Json's default DateTime converter requires strict
        // ISO 8601 ("2026-07-21T16:03:09"), rejecting a space separator
        // ("2026-07-21 16:03:09") with "The JSON value could not be
        // converted to System.Nullable`1[System.DateTime]".
        Book_Time: (() => {
          const now = new Date();
          const d = now.toISOString().split('T')[0];
          const t = now.toLocaleTimeString('en-CA', { hour12: false });
          return `${d}T${t}`;
        })(),
        Remarks: specialInst,
        Booking_Pincode: bookingPin,
        Delivery_Pincode: deliveryPin,
        Consnt_Delivery_Date: expDelivery,
        Job_id: parseInt(selectedJob) || null,
        Consignor_Id: parseInt(cnorSel) || null,
        Consignee_Id: parseInt(cneeSel) || null,
        Consignor_Contact: cnorData.contact,
        Consignor_Ph: cnorData.mobile ? cnorData.mobile.replace(/\D/g, "").slice(0, 15) : null,
        // Dedicated per-channel targets from the Booking Notifications panel —
        // these can differ from Consignor_Ph/consignor_Email, so send them
        // explicitly instead of letting the backend reuse Consignor_Ph for WhatsApp.
        // On Mobile Version there's no smsOn toggle to check (that panel isn't
        // shown there) — send directly off the Consignor Mobile field the
        // user actually typed into. Desktop keeps the original smsOn-gated
        // behavior unchanged.
        Sms_Number:   (isMobile ? Boolean(cnorData.mobile) : smsOn) ? ((isMobile ? cnorData.mobile : smsNum) ? (isMobile ? cnorData.mobile : smsNum).replace(/\D/g, "").slice(0, 15) : null) : null,
        // On Mobile Version there's no waOn toggle to check (that panel isn't
        // shown there) — send directly off the Consignor Mobile field the
        // user actually typed into. Desktop keeps the original waOn-gated
        // behavior unchanged.
        Wa_Number:    (isMobile ? Boolean(cnorData.mobile) : waOn) ? ((isMobile ? cnorData.mobile : waNum) ? (isMobile ? cnorData.mobile : waNum).replace(/\D/g, "").slice(0, 15) : null) : null,
        Email_Address: emailOn ? (emailAddr || null) : null,
        // Consignee's own notification targets — same gating (per-channel
        // toggle) as the Consignor fields above, but a distinct number/address.
        // Same Mobile-only bypass as Sms_Number above, but off the
        // Consignee's own Mobile field.
        Cnee_Sms_Number:   (isMobile ? Boolean(cneeData.mobile) : smsOn) ? ((isMobile ? cneeData.mobile : cneeSmsNum) ? (isMobile ? cneeData.mobile : cneeSmsNum).replace(/\D/g, "").slice(0, 15) : null) : null,
        // Same Mobile-only bypass as Wa_Number above, but off the
        // Consignee's own Mobile field.
        Cnee_Wa_Number:    (isMobile ? Boolean(cneeData.mobile) : waOn) ? ((isMobile ? cneeData.mobile : cneeWaNum) ? (isMobile ? cneeData.mobile : cneeWaNum).replace(/\D/g, "").slice(0, 15) : null) : null,
        Cnee_Email_Address: emailOn ? (cneeEmailAddr || null) : null,
        consignor_Add: cnorData.addr,
        Consignor_GSTN: cnorData.gst,
        Consignor_Pincode: cnorData.pin,
        consignor_Email: cnorData.email || null,
        consignee_Email: cneeData.email || null,
        Consignee_Contact: cneeData.contact,
        Consignee_Phone: cneeData.mobile ? cneeData.mobile.replace(/\D/g, "").slice(0, 15) : null,
        Consignee_Address: cneeData.addr,
        Consignee_GSTN: cneeData.gst,
        Consignee_Pincode: cneeData.pin,
        FREIGHT_TERMS_ID: parseInt(freight) || null,
        Priority_ID: parseInt(priority) || null,
        Desc: commodity,
        Declared_value: parseFloat(declVal) || null,
        Special_Instruction: specialInst,
        No_of_pkg: parseInt(tQ) || 0,
        Chrg_weight: parseFloat(tC) || 0,
        Rate_Basis: rateBasis,
        Base_Amt: parseFloat(rate.effectiveRate) || 0,
        Fuel_Sur_Per: parseInt(fuelPct) || 0,
        ST_Per: parseInt(gstPct) || 0,
        Other_charges: parseFloat(otherAmt) || 0,
        Freight_Charges: rate.freight,

        FS_Amt: rate.fuel,
        ST_AMT: rate.gst,
        // Other_charges_rate: rate.oa,
        Taxable_Amt: rate.sub,
        Amount: rate.total,
        // BUGFIX: this used to send the stray `otpRequired` state, which was
        // only ever set once (during Edit-load from the server) and had no
        // connection to the actual "Enable OTP for Delivery" toggle the user
        // sees and switches — so turning OTP on/off on screen never changed
        // what got saved. `otpOn` is the real toggle state, sent as a plain
        // boolean — Otp_Required is a System.Boolean on the server, so 1/0
        // fails deserialization ("could not be converted to System.Boolean").
        Otp_Required: !!otpOn,
  Otp_Mobile: otpMobile,
  Otp_Channel: otpChannel,
  Otp_Validity: otpValidity,
  Otp_Attempts: otpAttempts,
        Insurance: parseFloat(insAmt) || 0,
                VolumetricDetails: isVolumetric ? pieces.map(p => {
          const qty = +p.qty || 0, l = +p.l || 0, w = +p.w || 0, h = +p.h || 0, actW = +p.actW || 0;
          const volTot = volDivisor ? (l * w * h / volDivisor) * qty : 0;
          const actTot = actW * qty;
          const chgTot = Math.max(actTot, volTot);
          return {
            Description: p.desc,
            Pkg_Type: p.pkgType,
            Qty: qty,
            Length: l,
            Width: w,
            Height: h,
            Act_Wt_pc: actW,
            Vol_Wt_kg: volTot,
            Charged_Wt_kg: chgTot
          };
        }) : []
      };

      const response = await api.post('/save-cons-booking', [payload]);

      if (response.data.success) {

        if (images.length > 0) {

      // Step 1 — CNR number as folder name
      const consNo = cnr;   // e.g. STL-2506-00123

      // Step 2 — group by type
      const byType = {};
      images.forEach(img => {
        if (!byType[img.type]) byType[img.type] = [];
        byType[img.type].push(img);
      });

      // Step 3 & 4 — build FormData and call API for each type
      const uploadPromises = Object.entries(byType).map(([type, imgs]) => {
        const fd = new FormData();
        fd.append("consignment_no", consNo);
        fd.append("image_type",     type);
        imgs.forEach(img => fd.append("images", img.file, img.name));

        return api.post("/upload-cons-images", fd, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      });

      // Step 5 — wait for all uploads to finish
      try {
        await Promise.all(uploadPromises);
        addToast(`${images.length} image(s) uploaded`, "ok");
      } catch (imgErr) {
        addToast("Booking saved but images failed to upload", "warn");
      }
    }
        setConfirmOpen(false);
        // ── Scroll page to top so the success modal/sticker is visible
        //    regardless of where the user had scrolled to before booking ──
        scrollPageToTop();
        // ── Capture the booked CNR NOW before fetchAvailableConsignments refreshes cnr ──
        const justBookedCnr = cnr;
        const sd = {
          cnr: justBookedCnr,
          from: bookingPlaceOpts.find(o => String(o.value) === String(bookingPlace))?.label || PLACE_NAMES[bookingPlace] || "ORIGIN",
          to:   deliveryPlaceOpts.find(o => String(o.value) === String(deliveryPlace))?.label || PLACE_NAMES[deliveryPlace] || "DEST",
          cnor: cnorData.name || "—",
          cnee: cneeData.name || "—",
          pcs:  tQ + " pcs",
          wt:   tC.toFixed(2) + " kg",
          amt:  "₹ " + fmt(rate.total),
          date: todayStr,
        };
        setStickerData(sd);
        setBookedStickerData(sd);
        setBookedCnr(justBookedCnr);
        setBookedCnrForNotif(justBookedCnr);
        addToast(
          editingConsignmentId
            ? "Consignment updated successfully!"
            : "Consignment booked successfully!",
          "ok"
        );
        fetchAvailableConsignments(); // refresh CNR dropdown (changes cnr state — do AFTER capturing justBookedCnr)

        // NOTE: Email/WhatsApp/SMS are NOT re-sent from here. The backend's
        // /save-cons-booking (ConsBookingController.SaveConsBooking) already
        // sends SMS + WhatsApp + Email for both Consignor and Consignee,
        // gated by the exact same Send_*_Notification(_Consignee) flags in
        // the payload. Calling sendNotifEmail/sendNotifWa here as well was
        // hitting the same underlying SendEmailAsync/SendTemplateWhatsAppAsync
        // DAL methods a second time — i.e. every recipient got each
        // notification twice. If you need a manual "resend" action (e.g. a
        // button on the success screen), wire sendNotifEmail/sendNotifWa to
        // that explicit user action instead of firing them automatically here.

        setSuccessOpen(true);
      } else {
        addToast(response.data.message || "Booking failed", "err");
      }
    } catch (error) {
      console.error("Booking error:", error);
      console.error("Booking error — server response:", error.response?.data);
      addToast(error.response?.data?.message || "Failed to save booking", "err");
    } finally {
      setIsBooking(false);
    }
  }

  /* ── Barcode ── */
  useEffect(() => {
    if (stickerOpen && barcodeSvgRef.current && cnr) {
      setTimeout(() => {
        try {
          if (window.JsBarcode) {
            window.JsBarcode(barcodeSvgRef.current, cnr, {
              format: "CODE128", width: 2, height: 52, displayValue: false, margin: 4,
              lineColor: "#1A2940", background: "transparent",
            });
          }
        } catch (e) { }
      }, 100);
    }
  }, [stickerOpen, cnr]);

  /* ── Print sticker ── */
  function printSticker() {
    const c = document.getElementById("cb-sticker-print-inner").innerHTML;
    const w = window.open("", "_blank", "width=480,height=600");
    w.document.write(`<html><head><link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Lora:wght@700&display=swap" rel="stylesheet">
  <style>body{font-family:'Plus Jakarta Sans',sans-serif;margin:0;padding:20px;background:#fff;}
  .cb-sl-logo{display:flex;align-items:center;gap:8px;margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid #ddd;}
  .cb-sl-name{font-family:'Lora',serif;font-size:16px;font-weight:700;}
  .cb-s-cnr{font-size:22px;font-weight:800;letter-spacing:2px;text-align:center;margin:8px 0;}
  .cb-s-route{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;margin:10px 0;justify-content:center;}
  .cb-s-info{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:10px;}
  .cb-s-cell{background:#EBF4FF;border-radius:6px;padding:7px 9px;}
  .cb-s-cell-l{font-size:9px;font-weight:800;text-transform:uppercase;color:#8AA5C0;}
  .cb-s-cell-v{font-size:12px;font-weight:700;}svg{max-width:100%;}.cb-sl-mark{display:none;}</style>
  </head><body>${c}</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  }

  /* ══════════════════════════════════════════════════
     POST-BOOKING NOTIFICATION SENDERS
     (SMS removed — now sent automatically by the backend
     on booking save; only Email/WhatsApp remain manual.)
  ══════════════════════════════════════════════════ */

  // toEmail/cnrNo/sd/cnorName/cneeName are optional — when doBook() fires this
  // right after a successful save it passes them in directly (component state
  // like bookedCnrForNotif hasn't finished updating yet at that point). If
  // called with no args it falls back to state, same as the old behaviour.
  async function sendNotifEmail(toEmail, cnrNo, sd, cnorName, cneeName) {
    toEmail  = toEmail  ?? notifEmailVal;
    cnrNo    = cnrNo    ?? bookedCnrForNotif;
    sd       = sd       ?? bookedStickerData;
    cnorName = cnorName ?? cnorData.name;
    cneeName = cneeName ?? cneeData.name;
    if (!toEmail) return;

    setNotifSending(s => ({ ...s, email: true }));
    try {
      await api.post('api/SupportAdmin/SendNotification', {
        EventId: 0,
        ToEmail: toEmail,
        Subject: `Booking Confirmed — ${cnrNo}`,
        Body: `<div style="font-family:Segoe UI,sans-serif;max-width:520px;margin:0 auto;padding:24px;">
  <h2 style="color:#1a2a4a;margin-bottom:8px;">📦 Booking Confirmed</h2>
  <p style="color:#334155;">Dear <b>${cnorName}</b>,</p>
  <p style="color:#334155;">Your consignment has been booked successfully.</p>
  <table style="font-size:13px;color:#475569;line-height:2;width:100%;border-collapse:collapse;">
    <tr><td style="font-weight:700;padding-right:16px;width:140px;">CNR Number</td><td><b style="color:#1a2a4a;letter-spacing:1px;">${cnrNo}</b></td></tr>
    <tr><td style="font-weight:700;">Route</td><td>${sd.from} → ${sd.to}</td></tr>
    <tr><td style="font-weight:700;">Consignee</td><td>${cneeName}</td></tr>
    <tr><td style="font-weight:700;">Pieces</td><td>${sd.pcs}</td></tr>
    <tr><td style="font-weight:700;">Charged Weight</td><td>${sd.wt}</td></tr>
    <tr><td style="font-weight:700;">Amount</td><td>${sd.amt}</td></tr>
    <tr><td style="font-weight:700;">Date</td><td>${sd.date}</td></tr>
  </table>
  <hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0;">
  <p style="font-size:11px;color:#94a3b8;">Thank you for choosing Saami Tradestar Logistics Pvt Ltd.</p>
</div>`,
        EntityId: 0,
        Variables: {}
      });
      setNotifSent(s => ({ ...s, email: true }));
      addToast("Email sent successfully!", "ok");
    } catch (err) {
      addToast("Email failed: " + (err.response?.data?.message || err.message), "err");
    } finally {
      setNotifSending(s => ({ ...s, email: false }));
    }
  }

  async function sendNotifWa(waNumArg, cnrNo, sd, cnorName) {
    const rawMobile = waNumArg ?? notifWaNum;
    cnrNo    = cnrNo    ?? bookedCnrForNotif;
    sd       = sd       ?? bookedStickerData;
    cnorName = cnorName ?? cnorData.name;
    const mobile = (rawMobile || "").replace(/\D/g, "");
    if (!mobile) return;

    setNotifSending(s => ({ ...s, wa: true }));
    try {
      // Order MUST match saami_whatsapp_notification template placeholders {{1}} to {{8}}
      const bodyParams = [
        cnrNo,                                          // {{1}} AWB No
        sd.from,                                         // {{2}} Origin
        sd.to,                                           // {{3}} Destination
        sd.pcs,                                          // {{4}} QTY
        cnorName || sd.cnor,                              // {{5}} Consigner
        sd.cnee,                                          // {{6}} Consignee
        sd.date,                                          // {{7}} Booking Date and Time
        `https://saamitradestar.com/track/${cnrNo}`      // {{8}} Tracking link
      ];

      await api.post('api/SupportAdmin/SendWhatsAppNotification', {
        EventId: 0,
        MobileNo: mobile,
        TemplateName: "saami_whatsapp_notification",
        BodyParams: bodyParams,
        EntityId: 0
      });
      setNotifSent(s => ({ ...s, wa: true }));
      addToast("WhatsApp sent successfully!", "ok");
    } catch (err) {
      addToast("WhatsApp failed: " + (err.response?.data?.message || err.message), "err");
    } finally {
      setNotifSending(s => ({ ...s, wa: false }));
    }
  }

  function closeNotifModal() {
    setNotifModal({ open: false, email: false, wa: false });
    setSuccessOpen(true);
  }

  /* ── Clear form ── */
  function clearForm() {
    setBookingPlace(""); setBookingPin(""); setBpinInfo("");
    setDeliveryPlace(""); setDeliveryPin(""); setDpinInfo("");
    setPriority(""); setExpDelivery(calcExpDeliveryDate("standard", bookingDate));
    setPartyKey(""); setPartyGst(""); setSelectedJob(null); setFreight(""); setPartyJobs([]); setJobDivisors({ chrgwtAir: null, chrgwtSur: null });
    setCnorSel(""); setCnorData(emptyParty); setCnorMode("list");
    setCneeSel(""); setCneeData(emptyParty); setCneeMode("list");
    setOtpOn(false); setOtpMobile(""); setOtpChannel("both"); setOtpValidity("30"); setOtpAttempts("3"); setJobOtpMandatory(false);
    setNotifMaster(false);
    setSmsOn(notificationEvents.some(e => e.channelId === 1));
    setEmailOn(notificationEvents.some(e => e.channelId === 3));
    setWaOn(notificationEvents.some(e => e.channelId === 2));
    setSmsNum(""); setEmailAddr(""); setWaNum(""); setSelectedEvents([]);
    setPieces([]); setCommodity(""); setDeclVal(""); setSpecialInst("");
    setManualPcs(1); setManualChgWt(1); setIsVolumetric(false); setShipMode("");
    setRateBasis("perkg"); setBaseRate(0); setRateEdited(false); setFuelPct(12); setGstPct("5"); setInsAmt(0); setOtherAmt(0);
    setImages([]);
    setRawConsNo(null);
    setRawPrefix("");
    setEditingConsignmentId(null); // leaving edit mode — next Confirm & Book is a fresh insert
    addToast("Form cleared", "info");
  }

  function newBooking() {
    setStickerOpen(false);
    clearForm();
    setStep(1);
    fetchAvailableConsignments();
    setTimeout(() => {
      document.getElementById("cb-cnr-input")?.focus();
    }, 80);
  }

  /* ══════════════════════════════════════════════════
     BUILD SELECT OPTIONS (API first, static fallback)
  ══════════════════════════════════════════════════ */
  const placeOpts = [
    { value: "", label: "" },
    ...(bookingPlaceOpts.length > 0
      ? bookingPlaceOpts
      : Object.keys(PLACE_NAMES).map(k => ({ value: k, label: PLACE_NAMES[k] })))
  ];
  const delivPlaceOpts = [
    { value: "", label: "" },
    ...(deliveryPlaceOpts.length > 0
      ? deliveryPlaceOpts
      : Object.keys(PLACE_NAMES).map(k => ({ value: k, label: PLACE_NAMES[k] })))
  ];
  const partySelectOpts = [
    { value: "", label: "" },
    ...(partyOpts.length > 0
      ? partyOpts
      : Object.keys(PARTIES).map(k => ({ value: k, label: PARTIES[k].name })))
  ];

      const freightSelectOpts = freightTermOpts.length > 0
  ? freightTermOpts.map(o => ({
      value: o.freighT_TERMS_ID || o.id || o.FREIGHT_TERMS_ID,
      label: o.freighT_TERMS || o.name || o.FREIGHT_TERMS || o.label
    }))
  : [
      { value: "prepaid", label: "Prepaid (Sender pays)" },
      { value: "topay", label: "To Pay (Receiver)" },
      { value: "foc", label: "FOC (Free of Charge)" }
    ];
  const prioritySelectOpts = priorityOpts.length > 0
    ?priorityOpts.map(o => ({
        value: o.priority_ID || o.id || o.Priority_ID,
        label: o.priority_Value || o.value || o.Priority_Value
      }))
    : [
        { value: "standard", label: "Standard (3 days)" },
        { value: "express",  label: "Express (24 hrs)" },
        { value: "urgent",   label: "Urgent (Same Day)" },
      ];
  const cnorSelectOpts = [{ value: "", label: "" }, ...cnorOpts];
  const cneeSelectOpts = [{ value: "", label: "" }, ...cneeOpts];
  const jobOpts = partyKey
    ? (partyJobs.length > 0
        ? partyJobs.map(j => ({ value: j.job_Id || j.Job_Id, label: j.job_Name || j.Job_Name }))
        : (PARTY_JOBS[partyKey] || []).map(j => ({ value: j, label: j })))
    : [];
  const rateOpts       = [{ value: "perkg", label: "Per Kg" }, { value: "perpkg", label: "Per Package" }, { value: "fixed", label: "Fixed Rate" }];
  const gstOpts        = [{ value: "0", label: "0% (Exempt)" }, { value: "5", label: "5%" }, { value: "12", label: "12%" }, { value: "18", label: "18%" }];
  const otpChOpts      = [{ value: "both", label: "SMS + WhatsApp" }, { value: "sms", label: "SMS Only" }, { value: "wa", label: "WhatsApp Only" }];
  const otpValOpts     = [{ value: "15", label: "15 minutes" }, { value: "30", label: "30 minutes" }, { value: "60", label: "1 hour" }];
  const otpAttOpts     = [{ value: "3", label: "3 attempts" }, { value: "5", label: "5 attempts" }];

  /* ══════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════ */
  return (
    <div className="cb-root" onKeyDown={handleEnterKey}>
{/* 
      ── HEADER ──
      <header className="cb-hdr">
        <div className="cb-logo">
          <div className="cb-logo-mark">
            <svg viewBox="0 0 24 24">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
            </svg>
          </div>
          <div>
            <div className="cb-logo-name">Saami <span>Tradestar</span> Logistics</div>
            <div className="cb-logo-pvt">Pvt Ltd</div>
          </div>
        </div>
        <div className="cb-hdr-r">
          <span className="cb-hdr-pill">New Booking</span>
          <div className="cb-hdr-user">
            <div className="cb-hdr-av">RM</div>Rahul M.
          </div>
        </div>
      </header> */}

      {/* ── BREADCRUMB ── */}
      {/* <div className="cb-bc">
        <span>Operations</span>
        <span className="cb-bc-sep">›</span>
        <span>Consignments</span>
        <span className="cb-bc-sep">›</span>
        <span className="cb-bc-cur">New Booking</span>
      </div> */}

      <div
        className="cb-main"
        aria-hidden={confirmOpen || successOpen || stickerOpen || notifModal.open}
        style={(confirmOpen || successOpen || stickerOpen || notifModal.open)
          ? { pointerEvents: "none", userSelect: "none" }
          : undefined}
      >
        {view === "search" && (
          <div className="cb-sp">
            {/* ── PAGE TITLE ── */}
            <div className="cb-pg-row">
              <div className="cb-pg-title">
                <h1>Consignment Bookings</h1>
                <p>Search existing bookings by branch, party &amp; date — or start a new one</p>
              </div>
              <button className="cb-btn cb-btn--teal cb-btn--lg" onClick={startNewBooking}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add Booking
              </button>
            </div>

            {/* ── FILTERS ── */}
            <div className="cb-card">
              <div className="cb-band">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                  </svg>
                </div>
                <div>
                  <div className="cb-band-title">Search Filters</div>
                  <div className="cb-band-sub">Branch, Party &amp; Booking Date Range</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-g4">
                  <div className="cb-field">
                    <label>Branch</label>
                    <select value={searchBranchId} onChange={e => setSearchBranchId(e.target.value)}>
                      <option value="">All Branches</option>
                      {bookingPlaceOpts.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cb-field">
                    <label>Party</label>
                    <select value={searchPartyId} onChange={e => setSearchPartyId(e.target.value)}>
                      <option value="">All Parties</option>
                      {searchPartyOpts.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cb-field">
                    <label>Job / Department</label>
                    <select
                      value={searchJobId}
                      onChange={e => setSearchJobId(e.target.value)}
                      disabled={!searchPartyId || searchPartyJobsLoading}
                    >
                      <option value="">
                        {!searchPartyId ? "Select a party first…" : searchPartyJobsLoading ? "Loading jobs…" : "All Jobs"}
                      </option>
                      {searchJobOpts.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cb-field">
                    <label>Consignment No</label>
                    <input
                      type="text"
                      placeholder="Search by CNR No"
                      value={searchConsignmentNo}
                      onChange={e => setSearchConsignmentNo(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") runBookingSearch(); }}
                    />
                  </div>
                  <div className="cb-field">
                    <label>From Date</label>
                    <input type="date" value={searchFromDate} max={searchToDate || undefined}
                      onChange={e => setSearchFromDate(e.target.value)} />
                  </div>
                  <div className="cb-field">
                    <label>To Date</label>
                    <input type="date" value={searchToDate} min={searchFromDate || undefined}
                      onChange={e => setSearchToDate(e.target.value)} />
                  </div>
                  <div className="cb-field cb-field--inline">
                    <label>Bill Status</label>
                    <div className="cb-sw-radio-row">
                      <label className="cb-radio">
                        <input
                          type="radio"
                          name="cb-search-bill-status"
                          value=""
                          checked={searchBillStatus === ""}
                          onChange={() => setSearchBillStatus("")}
                        />
                        <span className="cb-radio-mark"></span>
                        All
                      </label>
                      <label className="cb-radio">
                        <input
                          type="radio"
                          name="cb-search-bill-status"
                          value="unbilled"
                          checked={searchBillStatus === "unbilled"}
                          onChange={() => setSearchBillStatus("unbilled")}
                        />
                        <span className="cb-radio-mark"></span>
                        Unbilled
                      </label>
                      <label className="cb-radio">
                        <input
                          type="radio"
                          name="cb-search-bill-status"
                          value="billed"
                          checked={searchBillStatus === "billed"}
                          onChange={() => setSearchBillStatus("billed")}
                        />
                        <span className="cb-radio-mark"></span>
                        Billed
                      </label>
                    </div>
                  </div>
                  <div className="cb-field" style={{ justifyContent: "flex-end" }}>
                    <label style={{ opacity: 0 }}>Search</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="cb-btn cb-btn--ghost" onClick={resetSearchFilters} disabled={searchLoading}>Reset</button>
                      <button className="cb-btn cb-btn--primary" onClick={runBookingSearch} disabled={searchLoading}>
                        {searchLoading ? "Searching..." : "Search"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── RESULTS ── */}
            <div className="cb-card">
              <div className="cb-band cb-band--teal">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16" />
                  </svg>
                </div>
                <div style={{ flex: 1 }}>
                  <div className="cb-band-title">Booking Results</div>
                  <div className="cb-band-sub">Click a row action to edit, preview or delete</div>
                </div>
                <span className="cb-sw-count">
                  {searchDone && !searchLoading
                    ? `${searchRows.length}${searchHasMore || searchTotalCount > searchRows.length ? ` of ${searchTotalCount}` : ""} result${searchTotalCount === 1 ? "" : "s"}`
                    : ""}
                </span>
              </div>
              <div className="cb-card-body">
                {searchLoading && (
                  <div className="cb-sw-state">Loading bookings…</div>
                )}
                {!searchLoading && searchError && (
                  <div className="cb-sw-state cb-sw-state--err">⚠ {searchError}</div>
                )}
                {!searchLoading && !searchError && searchDone && searchRows.length === 0 && (
                  <div className="cb-sw-state">No bookings found for the selected filters.</div>
                )}
                {!searchLoading && !searchError && searchRows.length > 0 && (
                  <div
                    className="cb-sw-scroll"
                    ref={searchScrollRef}
                    onScroll={handleSearchResultsScroll}
                  >
                    <table className="cb-pt cb-sw-tbl">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>CNR No</th>
                          <th>Book Date</th>
                          <th>Branch</th>
                          <th>Party</th>
                          <th>Consignor</th>
                          <th>Consignee</th>
                          <th>Pkgs</th>
                          <th>Chrg Wt</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchRows.map((row, idx) => {
                          const id = row.consignment_Id ?? row.Consignment_Id;
                          const billed = isSearchRowBilled(row);
                          return (
                            <tr key={id}>
                              <td style={{ textAlign: "right", color: "var(--muted)" }}>{idx + 1}</td>
                              <td style={{ fontWeight: 700, color: "var(--navy)" }}>{row.consignment_No ?? row.Consignment_No}</td>
                              <td>{formatDateDisplay(row.book_Dt ?? row.Book_Dt)}</td>
                              <td>{row.branchName ?? row.BranchName ?? "—"}</td>
                              <td>{row.partyName ?? row.PartyName ?? "—"}</td>
                              <td>{row.consignor_Name ?? row.Consignor_Name ?? "—"}</td>
                              <td>{row.consignee_Name ?? row.Consignee_Name ?? "—"}</td>
                              <td>{row.no_Of_pkg ?? row.No_of_pkg ?? 0}</td>
                              <td>{Number(row.chrg_Weight ?? row.Chrg_weight ?? 0).toFixed(2)}</td>
                              <td>₹ {Number(row.amount ?? row.Amount ?? 0).toFixed(2)}</td>
                              <td>
                                <span className={`cb-sw-badge ${billed ? "cb-sw-badge--billed" : "cb-sw-badge--open"}`}>
                                  {billed ? "Billed" : "Open"}
                                </span>
                              </td>
                              <td>
                                <div className="cb-sw-actions">
                                  <button className="cb-sw-icon cb-sw-icon--edit" title={billed ? "Billed — cannot edit" : "Edit"}
                                    disabled={billed} onClick={() => editSearchRow(row)}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                                    </svg>
                                  </button>
                                  <button className="cb-sw-icon cb-sw-icon--preview" title="Preview"
                                    onClick={() => openSearchPreview(row)}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
                                    </svg>
                                  </button>
                                  <button className="cb-sw-icon cb-sw-icon--delete" title={billed ? "Billed — cannot delete" : "Delete"}
                                    disabled={billed} onClick={() => setSearchDeleteRow(row)}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M3 6h18" /><path d="M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Batch-loading indicator — fires as the user scrolls near the bottom */}
                    {searchLoadingMore && (
                      <div className="cb-sw-more">
                        <span className="cb-sw-more-spin" />
                        Loading more bookings…
                      </div>
                    )}
                    {!searchLoadingMore && !searchHasMore && searchRows.length > SEARCH_PAGE_SIZE && (
                      <div className="cb-sw-more cb-sw-more--end">— End of results —</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {view === "form" && (
        <>
        {isMobile ? (
          <MobileFlatForm
            cnr={cnr} handleCnrChange={handleCnrChange} checkCnrDuplicate={checkCnrDuplicate}
            cnorData={cnorData} setCnorData={setCnorData}
            cneeData={cneeData} setCneeData={setCneeData}
            bookingDate={bookingDate} setBookingDate={setBookingDate} bookingTime={bookingTime}
            manualPcs={manualPcs} setManualPcs={setManualPcs}
            manualChgWt={manualChgWt} setManualChgWt={setManualChgWt}
            bookingPlace={bookingPlace} onBookingPlaceChange={v => handlePlaceChange("booking", v)} placeOpts={placeOpts}
            deliveryPlace={deliveryPlace} onDeliveryPlaceChange={v => handlePlaceChange("delivery", v)} delivPlaceOpts={delivPlaceOpts}
            partyKey={partyKey} handlePartyChange={handlePartyChange} partySelectOpts={partySelectOpts}
            selectedJob={selectedJob} handleJobChange={handleJobChange} jobOpts={jobOpts}
            onSubmit={handleMobileSubmit}
            isBooking={isBooking}
          />
        ) : (
        <>
        {/* ── PAGE TITLE ── */}
        <div className="cb-pg-row">
          <div className="cb-pg-title">
            <h1>New Consignment Booking</h1>
            <p>Fill shipment details to generate a consignment number and barcode sticker</p>
          </div>
          <button className="cb-btn cb-btn--ghost" onClick={() => setView("search")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Search
          </button>
        </div>

        {/* ── CNR BANNER ── */}
        <div className="cb-cnr-banner">
          <div style={{ flex: 1 }}>
            <div className="cb-cnr-lbl" style={{ textAlign: "left", marginLeft: 4 }}>Consignment Number</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
              <input
                id="cb-cnr-input"
                className="cb-cnr-inp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={cnr}
                onChange={e => handleCnrChange(e.target.value)}
                onBlur={async e => {
                  const val = e.target.value;
                  const isDup = await checkCnrDuplicate(val);
                  if (isDup) {
                    // Blur already happened by the time the async check resolves —
                    // pull focus back so the user can't drift away from an invalid CNR.
                    setTimeout(() => focusFieldWithHighlight("cb-cnr-input"), 0);
                  }
                }}
                onKeyDown={async e => {
                  // Enter / Tab (forward) → jump straight to Booking Place,
                  // skipping the Fetch button / Booking Date field that sit
                  // in between in DOM order (so neither the browser's default
                  // tab order nor the global Enter-to-next-field handler
                  // land on the Fetch button first).
                  if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
                    e.preventDefault();
                    e.stopPropagation();
                    const val = e.target.value;
                    // Empty CNR — nothing to check here, let step validation
                    // catch it (and focus this field) when they try to move on.
                    if (!val.trim()) return;
                    const isDup = await checkCnrDuplicate(val);
                    if (isDup) {
                      // Already booked — hold on this field, don't advance
                      addToast(`Consignment No ${val} is already booked. Please enter a different number.`, "warn");
                      focusFieldWithHighlight("cb-cnr-input");
                      return;
                    }
                    document.getElementById("cb-booking-place-input")?.focus();
                    return;
                  }
                  // Block non-numeric keystrokes outright (allow navigation/editing keys)
                  if (
                    ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key) ||
                    e.ctrlKey || e.metaKey || e.shiftKey
                  ) return;
                  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                }}
                onPaste={e => {
                  const text = (e.clipboardData || window.clipboardData).getData("text");
                  if (/\D/.test(text)) {
                    e.preventDefault();
                    handleCnrChange(text);
                  }
                }}
                placeholder={cnrLoading ? "Loading…" : "Enter or fetch a consignment number (numbers only)"}
                maxLength={30}
                style={{ width: "100%", maxWidth: 340 }}
              />
              <button
                type="button"
                onClick={fetchAvailableConsignments}
                disabled={cnrLoading}
                title="Fetch next available consignment number from backend"
                style={{
                  border: "1px solid var(--border, #DCE3EE)", background: "transparent",
                  borderRadius: 6, padding: "6px 10px", fontSize: 13, cursor: cnrLoading ? "default" : "pointer",
                  opacity: cnrLoading ? 0.6 : 1, whiteSpace: "nowrap", color: "rgba(255, 255, 255, 0.55)"
                }}
              >
                {cnrLoading ? "…" : "↻ Fetch"}
              </button>
              <span style={{ fontSize: 16, opacity: 0.7, marginLeft: 2 }} title="Auto-assigned, or type your own consignment number">✎</span>
            </div>
            {cnrChecking && (
              <div style={{ fontSize: 11.5, color: "var(--muted, #8A93A6)", marginTop: 5, marginLeft: 2 }}>
                Checking availability…
              </div>
            )}
            {!cnrChecking && cnrDuplicate && (
              <div style={{ fontSize: 11.5, color: "#E53E3E", marginTop: 5, marginLeft: 2, fontWeight: 600 }}>
                ⚠ Consignment No {cnr} is already booked. Please enter a different number.
              </div>
            )}
            {!cnrChecking && !cnrDuplicate && cnrEdited && cnr && (
              <div style={{ fontSize: 11.5, color: "var(--teal, #0D9488)", marginTop: 5, marginLeft: 2 }}>
                ✓ Manually entered — this number will be saved as entered.
              </div>
            )}
            {!cnrLoading && !cnr && (
              <div style={{ fontSize: 11.5, color: "#E53E3E", marginTop: 5, marginLeft: 2 }}>
                ⚠ No consignment number available. Fetch from backend or enter one manually.
              </div>
            )}
          </div>
          {/* <div className="cb-cnr-r">
            <div>
              <div className="cb-cnr-date-lbl">Booking Date</div>
              <input
                type="date"
                value={bookingDate}
                onChange={e => handleDateChange(e.target.value)}
                style={{ background: "transparent", border: "none", color: "rgba(255, 255, 255, 0.55)", fontWeight: 700, fontSize: 14, cursor: "pointer" }}
              />
            </div>
          </div> */}

          <div className="cb-cnr-r">
  <div>
    <div className="cb-cnr-date-lbl">Booking Date</div>
    <input
      type="date"
      value={bookingDate}
      onChange={e => handleDateChange(e.target.value)}
      style={{
        background: "transparent",
        border: "none",
        color: "rgba(255, 255, 255, 0.55)",
        fontWeight: 700,
        fontSize: 14,
        cursor: "pointer"
      }}
    />
  </div>
</div>
        </div>

        {jobOtpMandatory && (
          <div style={{
            background: "#FEF3C7",
            border: "1px solid #F59E0B",
            borderRadius: 8,
            padding: "10px 16px",
            margin: "10px 0",
            fontSize: 13,
            fontWeight: 700,
            color: "#92400E",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
            🔒 OTP is mandatory for this Job — Consignee must provide OTP before goods are handed over at delivery.
          </div>
        )}

        {/* ── STEPPER ── */}
        <div className="cb-stepper" ref={stepperRef}>
          {[
            { n: 1, name: "Route & Party",        desc: "Origin, destination & account" },
            { n: 2, name: "Consignor / Consignee", desc: "Sender, receiver & OTP" },
            { n: 3, name: "Shipment & Rate",       desc: "Pieces, images & billing" },
          ].map((s, i, arr) => (
            <>
              <div
                key={s.n}
                className={`cb-step${step === s.n ? " cb-step--active" : step > s.n ? " cb-step--done" : ""}`}
                onClick={() => goToStep(s.n)}
              >
                <div className="cb-step-num">{step > s.n ? "✓" : s.n}</div>
                <div>
                  <div className="cb-step-name">{s.name}</div>
                  <div className="cb-step-desc">{s.desc}</div>
                </div>
              </div>
              {i < arr.length - 1 && <div key={`div-${s.n}`} className="cb-step-div" />}
            </>
          ))}
        </div>

        {/* ════════ STEP 1 ════════ */}
        {step === 1 && (
          <div>
            {/* Route */}
            <div className="cb-card">
              <div className="cb-band">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Route Information</div>
                  <div className="cb-band-sub">Select origin, destination &amp; expected delivery</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-g3">
                  <div className="cb-field">
                    <label className="req">Booking Place</label>
                    <AutoComplete options={placeOpts} value={bookingPlace} onChange={v => handlePlaceChange("booking", v)} placeholder="Select branch…" inputId="cb-booking-place-input" />
                    {bpinInfo && <div className="cb-pin-tag on">{bpinInfo}</div>}
                  </div>
                  <div className="cb-field">
                    <label className="req">Booking Pincode</label>
                    <input id="cb-booking-pin-input" value={bookingPin} onChange={(e) => setBookingPin(e.target.value.replace(/\D/g, ""))} placeholder="6-digit PIN" maxLength={6} />
                  </div>
                  <div className="cb-field">
                    <label>Booking Date</label>
                    <input type="date" value={bookingDate} onChange={e => handleDateChange(e.target.value)} />
                  </div>
                  <div className="cb-field">
                    <label className="req">Delivery Place</label>
                    <AutoComplete options={delivPlaceOpts} value={deliveryPlace} onChange={v => handlePlaceChange("delivery", v)} placeholder="Select destination…" inputId="cb-delivery-place-input" />
                    {dpinInfo && <div className="cb-pin-tag on">{dpinInfo}</div>}
                  </div>
                  <div className="cb-field">
                    <label className="req">Destination Pincode</label>
                    <input id="cb-delivery-pin-input" value={deliveryPin} onChange={(e) => setDeliveryPin(e.target.value.replace(/\D/g, ""))} placeholder="6-digit PIN" maxLength={6} />
                  </div>
                  <div className="cb-field">
                    <label>Expected Delivery</label>
                    <input type="date" value={expDelivery} onChange={e => setExpDelivery(e.target.value)} placeholder="Auto-calculated" />
                  </div>
                </div>
              </div>
            </div>

            {/* Party */}
            <div className="cb-card">
              <div className="cb-band cb-band--teal">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Party &amp; Department</div>
                  <div className="cb-band-sub">Select billing account — one job per consignment</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-g3">
                  {/* Row 1: Party / Account | Party GSTIN | Job / Department */}
                  <div className="cb-field">
                    <label className="req">Party / Account</label>
                    <AutoComplete options={partySelectOpts} value={partyKey} onChange={handlePartyChange} placeholder="Search party…" inputId="cb-party-input" />
                  </div>
                  {!isMobile && (
                  <div className="cb-field">
                    <label>Party GSTIN</label>
                    <input value={partyGst} readOnly className="ro" placeholder="Auto-filled from party" />
                  </div>
                  )}
                  <div className="cb-field">
                    <label className="req">Job / Department</label>
                    <AutoComplete
                      options={[{ value: "", label: partyKey ? "" : "Select a party first…" }, ...jobOpts]}
                      value={selectedJob ?? ""}
                      onChange={handleJobChange}
                      placeholder={partyKey ? "Search job / department…" : "Select a party first…"}
                      inputId="cb-job-input"
                    />
                  </div>
                  {/* Row 2: Freight Terms | Priority | (empty — actions bar below) */}
                  <div className="cb-field">
                    <label>Freight Terms</label>
                    <AutoComplete options={freightSelectOpts} value={freight} onChange={setFreight} placeholder="Select" inputId="cb-freight-input" />
                  </div>
                  <div className="cb-field">
                    <label>Priority</label>
                    <AutoComplete
                      options={prioritySelectOpts}
                      value={priority}
                      onChange={handlePriorityChange}
                      placeholder="Select"
                      inputId="cb-priority-input"
                      onEnter={() => { document.getElementById("cb-next-btn")?.focus(); }}
                    />
                  </div>
                </div>

                {/* Actions bar — Clear + Next on same row, same original design */}
                <div className="cb-actions">
                  <button className="cb-btn cb-btn--ghost" onClick={clearForm}>✕ Clear</button>
                  <button
                    id="cb-next-btn"
                    tabIndex={0}
                    className="cb-btn cb-btn--primary"
                    onClick={() => goToStep(2)}
                  >
                    Next — Consignor / Consignee
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ════════ STEP 2 ════════ */}
        {step === 2 && (
          <div>
            <div className="cb-g2">
              {/* CONSIGNOR */}
              <div className="cb-card">
                <div className="cb-band">
                  <div className="cb-band-icon">
                    <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>
                  </div>
                  <div><div className="cb-band-title">Consignor</div><div className="cb-band-sub">Who is dispatching the goods</div></div>
                </div>
                <div className="cb-card-body">
                  <div className="cb-pb">
                    <div className="cb-pb-hdr">
                      <span className="cb-pb-lbl">Entry Mode</span>
                      <div className="cb-mode-tabs">
                        <button className={`cb-mode-tab${cnorMode === "list" ? " active" : ""}`} onClick={() => setCnorMode("list")}>From List</button>
                        <button className={`cb-mode-tab${cnorMode === "direct" ? " active" : ""}`} onClick={() => setCnorMode("direct")}>Direct Entry</button>
                      </div>
                    </div>
                    <div className="cb-pb-body">
                      {cnorMode === "list" && (
                        <div className="cb-field">
                          <label>Select Consignor</label>
                          <AutoComplete options={cnorSelectOpts} value={cnorSel} onChange={handleCnorSel} placeholder="Search consignor…" inputId="cb-cnor-ac-input" />
                        </div>
                      )}
                      <div className="cb-field"><label className="req">Name</label><input id="cb-cnor-name-input" value={cnorData.name} onChange={(e) => setCnorData(d => ({ ...d, name: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnor-contact-input")} placeholder="Company / person name" /></div>
                      {!isMobile && (<>
                      <div className="cb-g2">
                        <div className="cb-field"><label>Contact Person</label><input id="cb-cnor-contact-input" value={cnorData.contact} onChange={(e) => setCnorData(d => ({ ...d, contact: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnor-mobile-input")} placeholder="Contact name" /></div>
                        <div className="cb-field"><label>Mobile</label><input id="cb-cnor-mobile-input" value={cnorData.mobile} onChange={(e) => setCnorData(d => ({ ...d, mobile: e.target.value.replace(/\D/g, "") }))} onKeyDown={makeFieldAdvance("cb-cnor-addr-input")} placeholder="10-digit" maxLength={10} /></div>
                      </div>
                      <div className="cb-field"><label>Address</label><textarea id="cb-cnor-addr-input" value={cnorData.addr} onChange={(e) => setCnorData(d => ({ ...d, addr: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnor-gst-input")} placeholder="Full address" rows={2} /></div>
                      <div className="cb-g2">
                        <div className="cb-field"><label>GSTIN</label><input id="cb-cnor-gst-input" value={cnorData.gst} onChange={(e) => setCnorData(d => ({ ...d, gst: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnor-pin-input")} placeholder="15-char GST" maxLength={15} /></div>
                        <div className="cb-field"><label>Pincode</label><input id="cb-cnor-pin-input" value={cnorData.pin} onChange={(e) => setCnorData(d => ({ ...d, pin: e.target.value.replace(/\D/g, "") }))} onKeyDown={makeFieldAdvance("cb-cnor-email-input")} placeholder="6-digit" maxLength={6} /></div>
                      </div>
                      <div className="cb-field">
                        <label>Email</label>
                        <input
                          id="cb-cnor-email-input"
                          type="email"
                          value={cnorData.email}
                          onChange={(e) => setCnorData(d => ({ ...d, email: e.target.value }))}
                          onKeyDown={e => {
                            // Last consignor field — Enter / Tab (forward) hands
                            // off to the Consignee section, landing on whichever
                            // field is actually first there (the search box in
                            // "From List" mode, or Name in "Direct Entry" mode).
                            if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              const nextId = cneeMode === "list" ? "cb-cnee-ac-input" : "cb-cnee-name-input";
                              document.getElementById(nextId)?.focus();
                            }
                          }}
                          placeholder="name@example.com"
                        />
                      </div>
                      </>)}
                    </div>
                  </div>
                </div>
              </div>

              {/* CONSIGNEE */}
              <div className="cb-card">
                <div className="cb-band cb-band--teal">
                  <div className="cb-band-icon">
                    <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                  </div>
                  <div><div className="cb-band-title">Consignee</div><div className="cb-band-sub">Who will receive the shipment</div></div>
                </div>
                <div className="cb-card-body">
                  <div className="cb-pb">
                    <div className="cb-pb-hdr">
                      <span className="cb-pb-lbl">Entry Mode</span>
                      <div className="cb-mode-tabs">
                        <button className={`cb-mode-tab${cneeMode === "list" ? " active" : ""}`} onClick={() => setCneeMode("list")}>From List</button>
                        <button className={`cb-mode-tab${cneeMode === "direct" ? " active" : ""}`} onClick={() => setCneeMode("direct")}>Direct Entry</button>
                      </div>
                    </div>
                    <div className="cb-pb-body">
                      {cneeMode === "list" && (
                        <div className="cb-field">
                          <label>Select Consignee</label>
                          <AutoComplete options={cneeSelectOpts} value={cneeSel} onChange={handleCneeSel} placeholder="Search consignee…" inputId="cb-cnee-ac-input" />
                        </div>
                      )}
                      <div className="cb-field"><label className="req">Name</label><input id="cb-cnee-name-input" value={cneeData.name} onChange={(e) => setCneeData(d => ({ ...d, name: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnee-contact-input")} placeholder="Company / person name" /></div>
                      {!isMobile && (<>
                      <div className="cb-g2">
                        <div className="cb-field"><label>Contact Person</label><input id="cb-cnee-contact-input" value={cneeData.contact} onChange={(e) => setCneeData(d => ({ ...d, contact: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnee-mobile-input")} placeholder="Contact name" /></div>
                        <div className="cb-field"><label>Mobile</label><input id="cb-cnee-mobile-input" value={cneeData.mobile} onChange={(e) => setCneeData(d => ({ ...d, mobile: e.target.value.replace(/\D/g, "") }))} onKeyDown={makeFieldAdvance("cb-cnee-addr-input")} placeholder="10-digit" maxLength={10} /></div>
                      </div>
                      <div className="cb-field"><label>Address</label><textarea id="cb-cnee-addr-input" value={cneeData.addr} onChange={(e) => setCneeData(d => ({ ...d, addr: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnee-gst-input")} placeholder="Full address" rows={2} /></div>
                      <div className="cb-g2">
                        <div className="cb-field"><label>GSTIN</label><input id="cb-cnee-gst-input" value={cneeData.gst} onChange={(e) => setCneeData(d => ({ ...d, gst: e.target.value }))} onKeyDown={makeFieldAdvance("cb-cnee-pin-input")} placeholder="15-char GST" maxLength={15} /></div>
                        <div className="cb-field"><label>Pincode</label><input id="cb-cnee-pin-input" value={cneeData.pin} onChange={(e) => setCneeData(d => ({ ...d, pin: e.target.value.replace(/\D/g, "") }))} onKeyDown={makeFieldAdvance("cb-cnee-email-input")} placeholder="6-digit" maxLength={6} /></div>
                      </div>
                      <div className="cb-field">
                        <label>Email</label>
                        <input
                          id="cb-cnee-email-input"
                          type="email"
                          value={cneeData.email}
                          onChange={(e) => setCneeData(d => ({ ...d, email: e.target.value }))}
                          onKeyDown={e => {
                            // Enter / Tab (forward) → jump straight to the Next
                            // button, skipping past the OTP toggle and the
                            // entire Booking Notifications block (SMS/Email/
                            // WhatsApp fields + checkboxes) that sit in between
                            // in DOM order. stopPropagation is required here —
                            // without it the page's global Enter handler also
                            // fires on the bubble and re-focuses whatever the
                            // *actual* next DOM field is (e.g. SMS Mobile No.),
                            // silently undoing this focus() call.
                            if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              document.getElementById("cb-step2-next-btn")?.focus();
                            }
                          }}
                          placeholder="name@example.com"
                        />
                      </div>
                      </>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* OTP — hidden on Mobile Version */}
            {!isMobile && (
            <div className="cb-card">
              <div className="cb-band">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">OTP Delivery Confirmation</div>
                  <div className="cb-band-sub">Optional — require OTP from consignee at delivery point</div>
                </div>
              </div>
              <div className="cb-card-body" style={{ paddingBottom: 0 }}>
                <div className="cb-otp-wrap">
                  <div className="cb-otp-hdr">
                    <div className="cb-otp-hdr-l">
                      <Toggle checked={otpOn} onChange={handleToggleOTP} disabled={jobOtpMandatory} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>Enable OTP for Delivery</div>
                        <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
                          {jobOtpMandatory
                            ? "Mandatory for this Job — cannot be turned off"
                            : "Consignee must provide OTP before goods are handed over"}
                        </div>
                      </div>
                    </div>
                    <span className="cb-otp-status">{otpOn ? "On" : "Off"}</span>
                  </div>
                  {otpOn && (
                    <div className="cb-otp-body">
                      <div className="cb-otp-info">🔐 An OTP is generated at delivery and sent to consignee mobile. Agent must verify before handing over.</div>
                      <div className="cb-g2">
                        <div className="cb-field"><label className="req">Consignee OTP Mobile</label><input id="cb-otp-mobile-input" value={otpMobile} onChange={(e) => setOtpMobile(e.target.value.replace(/\D/g, ""))} placeholder="10-digit number" maxLength={10} /></div>
                        <div className="cb-field"><label>OTP Channel</label><AutoComplete options={otpChOpts} value={otpChannel} onChange={setOtpChannel} /></div>
                        <div className="cb-field"><label>OTP Validity</label><AutoComplete options={otpValOpts} value={otpValidity} onChange={setOtpValidity} /></div>
                        <div className="cb-field"><label>Max Attempts</label><AutoComplete options={otpAttOpts} value={otpAttempts} onChange={setOtpAttempts} /></div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Notifications — hidden on Mobile Version */}
            {!isMobile && (
            <div className="cb-card">
              <div className="cb-band cb-band--teal" style={{ padding: "0 18px", minHeight: 52 }}>
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Booking Notifications</div>
                  <div className="cb-band-sub">Optional — notify consignor at each milestone</div>
                </div>
                <div className="cb-band-r">
                  <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.55)" }}>{notifMaster ? "ON" : "OFF"}</span>
                  <Toggle checked={notifMaster} onChange={setNotifMaster} />
                </div>
              </div>
              {notifMaster && (
                <div className="cb-notif-grid">
                  {/* SMS */}
                  <div className={`cb-nc${smsOn ? " on" : ""}`}>
                    <div className="cb-nc-hdr">
                      <div className="cb-nc-ico sms-ico">📱</div>
                      <div className="cb-nc-title">{getTitle(1, "SMS")}</div>
                      <Toggle checked={smsOn} onChange={setSmsOn} />
                    </div>
                    {smsOn && (
                      <div className="cb-nc-body">
                        <div className="cb-field"><label>Mobile No. (Consignor)</label><input value={smsNum} onChange={(e) => setSmsNum(e.target.value)} placeholder="10-digit" /></div>
                        <div className="cb-field"><label>Mobile No. (Consignee)</label><input value={cneeSmsNum} onChange={(e) => setCneeSmsNum(e.target.value)} placeholder="10-digit" /></div>
                        {getEventsByChannel(1).map(e => {
                          const id = e.eventId;
                          const name = e.eventName;
                          return (<label key={id} className="cb-nc-ev"><input type="checkbox" checked={selectedEvents.includes(id)} onChange={() => toggleEvent(id)} /> {name}</label>);
                        })}
                      </div>
                    )}
                  </div>
                  {/* Email */}
                  <div className={`cb-nc${emailOn ? " on" : ""}`}>
                    <div className="cb-nc-hdr">
                      <div className="cb-nc-ico em-ico">✉️</div>
                      <div className="cb-nc-title">{getTitle(3, "Email")}</div>
                      <Toggle checked={emailOn} onChange={setEmailOn} />
                    </div>
                    {emailOn && (
                      <div className="cb-nc-body">
                        <div className="cb-field"><label>Email Address (Consignor)</label><input value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} placeholder="email@company.com" /></div>
                        <div className="cb-field"><label>Email Address (Consignee)</label><input value={cneeEmailAddr} onChange={(e) => setCneeEmailAddr(e.target.value)} placeholder="email@company.com" /></div>
                        {getEventsByChannel(3).map(e => {
                          const id = e.eventId;
                          const name = e.eventName;
                          return (<label key={id} className="cb-nc-ev"><input type="checkbox" checked={selectedEvents.includes(id)} onChange={() => toggleEvent(id)} /> {name}</label>);
                        })}
                      </div>
                    )}
                  </div>
                  {/* WhatsApp */}
                  <div className={`cb-nc${waOn ? " on" : ""}`}>
                    <div className="cb-nc-hdr">
                      <div className="cb-nc-ico wa-ico">💬</div>
                      <div className="cb-nc-title">{getTitle(2, "WhatsApp")}</div>
                      <Toggle checked={waOn} onChange={setWaOn} />
                    </div>
                    {waOn && (
                      <div className="cb-nc-body">
                        <div className="cb-field"><label>WhatsApp No. (Consignor)</label><input value={waNum} onChange={(e) => setWaNum(e.target.value)} placeholder="91XXXXXXXXXX" /></div>
                        <div className="cb-field"><label>WhatsApp No. (Consignee)</label><input value={cneeWaNum} onChange={(e) => setCneeWaNum(e.target.value)} placeholder="91XXXXXXXXXX" /></div>
                        {getEventsByChannel(2).map(e => {
                          const id = e.eventId;
                          const name = e.eventName;
                          return (<label key={id} className="cb-nc-ev"><input type="checkbox" checked={selectedEvents.includes(id)} onChange={() => toggleEvent(id)} /> {name}</label>);
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}

            <div className="cb-actions">
              <button className="cb-btn cb-btn--ghost" onClick={() => { setCnorMode("list"); setCneeMode("list"); setStep(1); }}>← Back</button>
              <button id="cb-step2-next-btn" className="cb-btn cb-btn--primary" onClick={() => goToStep(3)}>
                Next — Shipment Details
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}

        {/* ════════ STEP 3 ════════ */}
        {step === 3 && (
          <div>
            {/* Pieces */}
            <div className="cb-card">
              <div className="cb-band cb-band--amber">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Piece-wise Shipment Details</div>
                  <div className="cb-band-sub">
                    {isVolumetric
                      ? `Vol. Wt = L×W×H ÷ ${volDivisor || "—"} (${shipMode ? (shipMode === "air" ? "Air" : "Surface") : "select mode"}) × Qty`
                      : "Vol. Wt = L×W×H ÷ divisor × Qty"} &nbsp;|&nbsp; Charged = higher of Actual vs Volumetric
                  </div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-g3" style={{ marginBottom: 14 }}>
                  {!isMobile && (<>
                  <div className="cb-field cb-gc2">
                    <label className="req">Commodity / Description</label>
                    <input id="cb-commodity-input" value={commodity} onChange={(e) => setCommodity(e.target.value)} placeholder="e.g. Engine Parts, Brake Pads, Clutch Assemblies" />
                  </div>
                  <div className="cb-field">
                    <label>Declared Value (₹)</label>
                    <input type="number" value={declVal} onChange={(e) => setDeclVal(e.target.value)} placeholder="0" />
                  </div>
                  </>)}
                  {!isMobile && (
                  <div className="cb-field cb-gc3">
                    <label>Special Instructions</label>
                    <input value={specialInst} onChange={(e) => setSpecialInst(e.target.value)} placeholder="e.g. Fragile — Handle with care, Keep upright…" />
                  </div>
                  )}
                  
                  <div className="cb-field"><label className="req">No. of PCs</label><input id="cb-manual-pcs-input" type="number" value={isVolumetric ? tQ : manualPcs} disabled={isVolumetric} placeholder="0" onChange={e => setManualPcs(e.target.value)} /></div>
                  <div className="cb-field">
                    <label className="req">Charged Weight (kg)</label>
                    <input
                      id="cb-chg-wt-input"
                      type="number"
                      value={isVolumetric ? tC : manualChgWt}
                      disabled={isVolumetric}
                      placeholder="0"
                      onChange={e => setManualChgWt(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Tab" || e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation(); // prevent global handleEnterKey from also firing and skipping past Rate Basis
                          document.getElementById("cb-rate-basis-input")?.focus();
                        }
                      }}
                      style={isVolumetric ? { background: "#f1f8e9", fontWeight: 600 } : {}}
                    />
                  </div>
                  <div className="cb-field">
                    <label>&nbsp;</label>
                    <div style={{ display: 'flex', alignItems: 'center', height: '38px' }}>
                      <label className="cb-sw" style={{ marginRight: '8px' }}>
                        <input
                          type="checkbox"
                          checked={isVolumetric}
                          onChange={e => {
                            const checked = e.target.checked;
                            setIsVolumetric(checked);
                            // Start with a clean slate every time volumetric mode is
                            // switched on — any piece rows left over from a previous
                            // toggle (or a previous booking) should not reappear.
                            if (checked) setPieces([]);
                            // Air / Surface only applies once volumetric weight is
                            // in use — clear any earlier pick when it's switched off.
                            if (!checked) setShipMode("");
                          }}
                        />
                        <span className="cb-sw-track"></span>
                      </label>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--ink)' }}>Volumetric Weight</span>
                    </div>
                  </div>
                </div>

         {isVolumetric && (
                          <>

                <div className="cb-shipmode-row" id="cb-shipmode-row" tabIndex={-1}>
                  <span className="cb-shipmode-lbl">Ship Via</span>
                  <label className="cb-radio">
                    <input
                      type="radio"
                      name="cb-ship-mode"
                      value="air"
                      checked={shipMode === "air"}
                      onChange={() => setShipMode("air")}
                    />
                    <span className="cb-radio-mark"></span>
                    Air
                  </label>
                  <label className="cb-radio">
                    <input
                      type="radio"
                      name="cb-ship-mode"
                      value="surface"
                      checked={shipMode === "surface"}
                      onChange={() => setShipMode("surface")}
                    />
                    <span className="cb-radio-mark"></span>
                    Surface
                  </label>

                  {/* Divisor status — fetched from JOB_Master via
                      /get-job-charged-weights/{job_id} once a Job is picked in Step 1 */}
                  {jobDivisorsLoading && (
                    <span className="cb-shipmode-status cb-shipmode-status--info">Fetching divisor…</span>
                  )}
                  {!jobDivisorsLoading && shipMode && volDivisorReady && (
                    <span className="cb-shipmode-status cb-shipmode-status--ok">
                      ✓ Divisor: {volDivisor}
                    </span>
                  )}
                  {!jobDivisorsLoading && shipMode && !volDivisorReady && (
                    <span className="cb-shipmode-status cb-shipmode-status--warn">
                      ⚠ No {shipMode === "air" ? "Air" : "Surface"} charged-weight value found for this Job — piece entry is disabled
                    </span>
                  )}
                </div>

                {isVolumetric && !volDivisorReady && (
                  <div className="cb-shipmode-placeholder">
                    {shipMode
                      ? "This Job has no volumetric divisor configured for the selected mode. Contact admin, or switch mode / job."
                      : "Select Air or Surface above to start adding pieces."}
                  </div>
                )}

                {volDivisorReady && (
                <div className="cb-piece-scroll">
                  <table className="cb-pt">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th style={{ textAlign: "left", minWidth: 128 }}>Description</th>
                        <th style={{ minWidth: 108 }}>Pkg Type</th>
                        <th style={{ minWidth: 55 }}>Qty</th>
                        <th style={{ minWidth: 64 }}>L (cm)</th>
                        <th style={{ minWidth: 64 }}>W (cm)</th>
                        <th style={{ minWidth: 64 }}>H (cm)</th>
                        <th style={{ minWidth: 76 }}>Act Wt/pc</th>
                        <th className="cb-vol-cell" style={{ minWidth: 82 }}>Vol Wt (kg)</th>
                        <th className="cb-chg-cell" style={{ minWidth: 86 }}>Chg Wt (kg)</th>
                        <th style={{ width: 36 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {pieces.map((row, i) => (
                        <PieceRow
                          key={row.id}
                          idx={i}
                          row={row}
                          divisor={volDivisor}
                          onChange={(updated) => setPieces(p => p.map(x => x.id === row.id ? updated : x))}
                          onDelete={() => setPieces(p => p.filter(x => x.id !== row.id))}
                        />
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="row-sub">
                        <td colSpan={3} className="tot-lbl">Sub Total</td>
                        <td>{tQ}</td>
                        <td colSpan={3}></td>
                        <td>{tA.toFixed(2)} kg</td>
                        <td>{tV.toFixed(2)} kg</td>
                        <td style={{ color: "var(--blue)" }}>{tC.toFixed(2)} kg</td>
                        <td></td>
                      </tr>
                      <tr className="row-grand">
                        <td colSpan={3} style={{ textAlign: "left", paddingLeft: 10 }}>GRAND TOTAL</td>
                        <td style={{ fontSize: 15 }}>{tQ}</td>
                        <td colSpan={3}></td>
                        <td style={{ fontSize: 15 }}>{tA.toFixed(2)} kg</td>
                        <td style={{ fontSize: 15 }}>{tV.toFixed(2)} kg</td>
                        <td style={{ fontSize: 15, color: "#5eead4" }}>{tC.toFixed(2)} kg</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="cb-pt-bar">
                    <span>💡 Volumetric Weight = L×W×H ÷ {volDivisor} per piece</span>
                    <div style={{ display: "flex", gap: 7 }}>
                      <button className="cb-btn cb-btn--ghost cb-btn--sm" onClick={addPiece} disabled={!volDivisorReady}>+ Add Piece</button>
                      <button className="cb-btn cb-btn--ghost cb-btn--sm" style={{ color: "var(--red)" }} onClick={() => setPieces([])}>Clear All</button>
                    </div>
                  </div>
                </div>
                )}
                {volDivisorReady && (
                <div className="cb-wt-strip">
                  <div className="cb-wt-box bx-blue"><div className="cb-wb-lbl">Total Pieces</div><div className="cb-wb-val">{tQ}</div><div className="cb-wb-unit">packages</div></div>
                  <div className="cb-wt-box bx-amber"><div className="cb-wb-lbl">Actual Weight</div><div className="cb-wb-val">{tA.toFixed(2)}</div><div className="cb-wb-unit">kg</div></div>
                  <div className="cb-wt-box bx-blue"><div className="cb-wb-lbl">Volumetric Weight</div><div className="cb-wb-val">{tV.toFixed(2)}</div><div className="cb-wb-unit">kg</div></div>
                  <div className="cb-wt-box bx-teal"><div className="cb-wb-lbl">✓ Charged Weight</div><div className="cb-wb-val">{tC.toFixed(2)}</div><div className="cb-wb-unit">kg — billing basis</div></div>
                </div>
                )}
              </>
              )}
            </div>
           </div>


            {/* Images */}
            <div className="cb-card">
              <div className="cb-band">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Shipment Images</div>
                  <div className="cb-band-sub">Attach photos of packages, labels, invoices or damage evidence</div>
                </div>
              </div>
              <div className="cb-card-body">
                <div className="cb-img-type-row">
                  {["Package", "Label", "Invoice", "Damage", "Other"].map((t) => (
                    <button key={t} className={`cb-itype${imgType === t ? " active" : ""}`} onClick={() => setImgType(t)}>
                      {t === "Package" ? "📦" : t === "Label" ? "🏷" : t === "Invoice" ? "📄" : t === "Damage" ? "⚠️" : "📎"} {t}
                    </button>
                  ))}
                </div>
                <div
                  className="cb-drop-zone"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("drag"); }}
                  onDragLeave={(e) => e.currentTarget.classList.remove("drag")}
                  onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("drag"); handleFiles(e.dataTransfer.files); }}
                >
                  <input type="file" accept="image/*,.pdf" multiple onChange={(e) => handleFiles(e.target.files)} />
                  <div className="cb-dz-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>Drag &amp; drop here, or click to browse</div>
                  <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Type: <strong>{imgType}</strong> &nbsp;|&nbsp; JPG · PNG · PDF · Max 5 MB each</div>
                </div>
                {images.length > 0 && (
                  <div className="cb-img-grid">
                    {images.map((img) => (
                      <div key={img.id} className="cb-img-item">
                        {img.isImg
                          ? <img src={img.src} alt={img.name} />
                          : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", fontSize: 26 }}>📄</div>}
                        <span className="cb-img-type-tag">{img.type}</span>
                        <div className="cb-img-lbl">{img.name}</div>
                        <button className="cb-img-del" onClick={() => setImages(imgs => imgs.filter(x => x.id !== img.id))}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {images.length > 0 && (
                  <div className="cb-img-count-bar">
                    <span>{images.length} file{images.length !== 1 ? "s" : ""} attached</span>
                    <button className="cb-btn cb-btn--ghost cb-btn--sm" style={{ color: "var(--red)" }} onClick={() => setImages([])}>Remove All</button>
                  </div>
                )}
              </div>
            </div>

            {/* Rate */}
            <div className="cb-card">
              <div className="cb-band cb-band--green">
                <div className="cb-band-icon">
                  <svg viewBox="0 0 24 24" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
                </div>
                <div>
                  <div className="cb-band-title">Rate Calculation</div>
                  <div className="cb-band-sub">Auto-computed from party rate card — override allowed with approval</div>
                </div>
              </div>
              <div className="cb-card-body">
                {/* Rate Slab Info Banner */}
                {rateLoading && (
                  <div style={{ padding: "8px 14px", background: "#e8f4fd", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "#1976d2", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ animation: "spin 1s linear infinite", display: "inline-block" }}>⏳</span>
                    Loading rate details…
                  </div>
                )}
                {fetchedRateHeader && !rateLoading && (
                  <div style={{ padding: "10px 14px", background: "linear-gradient(135deg, #e8f5e9, #f1f8e9)", borderRadius: 8, marginBottom: 12, fontSize: 12, border: "1px solid #c8e6c9" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <span style={{ fontSize: 15 }}>✅</span>
                      <strong style={{ color: "#2e7d32" }}>Rate Card Loaded</strong>
                      {fetchedRateHeader.currency_Code && <span style={{ marginLeft: "auto", background: "#fff", padding: "2px 8px", borderRadius: 4, fontWeight: 600, color: "#1b5e20" }}>{fetchedRateHeader.currency_Code}</span>}
                    </div>
                    <div style={{ color: "#4a4a4a", lineHeight: 1.6 }}>
                      {fetchedRateHeader.validity_Name && <span>Validity: <strong>{fetchedRateHeader.validity_Name}</strong> · </span>}
                      {fetchedRateHeader.effective_From && <span>Effective: <strong>{formatDateDisplay(fetchedRateHeader.effective_From)}</strong></span>}
                      {fetchedRateHeader.effective_To && <span> → <strong>{formatDateDisplay(fetchedRateHeader.effective_To)}</strong></span>}
                      {fetchedRateHeader.min_Charg_Wgt > 0 && <span> · Min Chg Wt: <strong>{fetchedRateHeader.min_Charg_Wgt} kg</strong></span>}
                    </div>
                    {fetchedRateHeader.rate_Remark && <div style={{ marginTop: 4, fontStyle: "italic", color: "#666" }}>📝 {fetchedRateHeader.rate_Remark}</div>}
                  </div>
                )}
                {!fetchedRateHeader && !rateLoading && partyKey && bookingPlace && deliveryPlace && (
                  <div style={{ padding: "8px 14px", background: "#fff3e0", borderRadius: 8, marginBottom: 12, fontSize: 12, color: "#e65100", border: "1px solid #ffe0b2" }}>
                    ⚠️ No rate card found for this party/route. Using manual base rate.
                  </div>
                )}

                {/* Rate Slab Table */}
                {fetchedRateDetails.length > 0 && (
                  <div style={{ marginBottom: 16, overflowX: "auto" }}>
                    <table className="cb-tbl" style={{ fontSize: 11.5, width: "100%" }}>
                      <thead>
                        <tr>
                          <th style={{ padding: "6px 10px" }}>Slab</th>
                          <th style={{ padding: "6px 10px" }}>From (kg)</th>
                          <th style={{ padding: "6px 10px" }}>To (kg)</th>
                          <th style={{ padding: "6px 10px" }}>Rate (₹)</th>
                          <th style={{ padding: "6px 10px" }}>Addl Wt Rate</th>
                          <th style={{ padding: "6px 10px" }}>UOM</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fetchedRateDetails.map((d, i) => {
                          const fromW = parseFloat(d.from_Wgt ?? d.From_Wgt) || 0;
                          const toW = parseFloat(d.to_Wgt ?? d.To_Wgt) || 0;
                          const isActive = tC >= fromW && tC <= toW;
                          return (
                            <tr key={d.rate_detailId ?? d.Rate_detailId ?? i}
                                style={isActive ? { background: "#e8f5e9", fontWeight: 600 } : {}}>
                              <td style={{ padding: "5px 10px", textAlign: "center" }}>{i + 1}</td>
                              <td style={{ padding: "5px 10px", textAlign: "right" }}>{fromW.toFixed(2)}</td>
                              <td style={{ padding: "5px 10px", textAlign: "right" }}>{toW.toFixed(2)}</td>
                              <td style={{ padding: "5px 10px", textAlign: "right", color: isActive ? "#2e7d32" : "inherit" }}>
                                ₹ {(parseFloat(d.rateAmount ?? d.RateAmount) || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: "5px 10px", textAlign: "right" }}>
                                {(parseFloat(d.addl_Wgt ?? d.Addl_Wgt) || 0) > 0
                                  ? `₹ ${(parseFloat(d.addl_Wgt ?? d.Addl_Wgt)).toFixed(2)}`
                                  : "—"}
                              </td>
                              <td style={{ padding: "5px 10px", textAlign: "center" }}>{d.uoM_Code ?? d.UOM_Code ?? "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {rate.slabMatch && (
                      <div style={{ marginTop: 6, fontSize: 11.5, color: "#2e7d32", fontWeight: 500 }}>
                        ✓ Matched slab: {rate.slabMatch.fromWgt}–{rate.slabMatch.toWgt} kg → ₹ {rate.effectiveRate.toFixed(2)} / unit
                      </div>
                    )}
                  </div>
                )}

                <div className="cb-g2">
                  <div className="cb-g2">
                    <div className="cb-field"><label>Rate Basis</label><AutoComplete options={rateOpts} value={rateBasis} onChange={setRateBasis} inputId="cb-rate-basis-input" /></div>
                    <div className="cb-field">
                      <label>Base Rate (₹) {rate.slabMatch && !rateEdited ? <span style={{ fontSize: 10, color: "#2e7d32", fontWeight: 500 }}> — auto from slab, editable</span> : ""}</label>
                      <input
                        id="cb-base-rate-input"
                        type="number"
                        value={rate.slabMatch && !rateEdited ? rate.effectiveRate.toFixed(2) : baseRate}
                        onChange={(e) => { setBaseRate(e.target.value); setRateEdited(true); }}
                        placeholder="Auto from rate card"
                        style={rate.slabMatch && !rateEdited ? { background: "#f1f8e9", fontWeight: 600 } : {}}
                      />
                    </div>
                    {!isMobile && (
                    <div className="cb-field"><label>Fuel Surcharge %</label><input type="number" value={fuelPct} onChange={(e) => setFuelPct(e.target.value)} step={0.5} /></div>
                    )}
                    <div className="cb-field"><label>GST %</label><AutoComplete options={gstOpts} value={gstPct} onChange={setGstPct} /></div>
                    {!isMobile && (
                    <div className="cb-field"><label>Insurance (₹)</label><input type="number" value={insAmt} onChange={(e) => setInsAmt(e.target.value)} placeholder="0" /></div>
                    )}
                    {!isMobile && (
                    <div className="cb-field">
                      <label>Other Charges (₹)</label>
                      <input
                        type="number"
                        value={otherAmt}
                        onChange={(e) => setOtherAmt(e.target.value)}
                        placeholder="0"
                        onKeyDown={e => {
                          if (e.key === "Tab" || e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation(); // skip the ← Back button, jump straight to Confirm & Book
                            document.getElementById("cb-confirm-book-btn")?.focus();
                          }
                        }}
                      />
                    </div>
                    )}
                  </div>
                  <div className="cb-rate-box">
                    {[
                      ["Freight Charge", rate.freight],
                      ["Fuel Surcharge",  rate.fuel],
                      ["Insurance",       rate.ia],
                      ["Other Charges",   rate.oa],
                      ["Sub Total",       rate.sub],
                      [`GST (${gstPct}%)`, rate.gst],
                    ].map(([lbl, val]) => (
                      <div key={lbl} className="cb-r-row">
                        <span className="cb-r-lbl">{lbl}</span>
                        <span className="cb-r-val">₹ {fmt(val)}</span>
                      </div>
                    ))}
                    <div className="cb-r-row cb-r-total">
                      <span className="cb-r-lbl">Total Payable</span>
                      <span className="cb-r-val">₹ {fmt(rate.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="cb-actions">
              <button className="cb-btn cb-btn--ghost" onClick={() => setStep(2)}>← Back</button>
              <button id="cb-confirm-book-btn" className="cb-btn cb-btn--teal cb-btn--lg" onClick={openConfirm}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Confirm &amp; Book
              </button>
            </div>
          </div>
        )}
        </>
        )}
        </>
        )}
      </div>

      {/* ════════ SEARCH: PREVIEW MODAL ════════ */}
      {searchPreviewRow && (
        <div className="cb-overlay show">
          <div className="cb-modal">
            <div className="cb-modal-hd">
              <h3>Consignment Preview</h3>
              <button className="cb-modal-x" onClick={() => setSearchPreviewRow(null)}>✕</button>
            </div>
            <div className="cb-modal-body">
              {searchPreviewLoading ? (
                <div className="cb-sw-state">Loading details…</div>
              ) : (
                <div className="cb-m-details">
                  {[
                    ["CNR Number", searchPreviewRow.consignment_No ?? searchPreviewRow.Consignment_No],
                    ["Booking Date", formatDateDisplay(searchPreviewRow.book_Dt ?? searchPreviewRow.Book_Dt)],
                    ["Consignor", searchPreviewRow.consignor_Name ?? searchPreviewRow.Consignor_Name],
                    ["Consignee", searchPreviewRow.consignee_Name ?? searchPreviewRow.Consignee_Name],
                    ["Packages", searchPreviewRow.no_Of_pkg ?? searchPreviewRow.No_of_pkg],
                    ["Charged Weight", `${Number(searchPreviewRow.chrg_Weight ?? searchPreviewRow.Chrg_weight ?? 0).toFixed(2)} kg`],
                    ["Taxable Amount", `₹ ${Number(searchPreviewRow.taxable_Amt ?? searchPreviewRow.Taxable_Amt ?? 0).toFixed(2)}`],
                    ["Total Amount", `₹ ${Number(searchPreviewRow.amount ?? searchPreviewRow.Amount ?? 0).toFixed(2)}`],
                    ["POD Available", (searchPreviewRow.pod_Available ?? searchPreviewRow.pod_available) ? "Yes" : "No"],
                  ].map(([lbl, val]) => (
                    <div key={lbl} className="cb-m-row">
                      <span className="ml">{lbl}</span>
                      <span className="mv">{val ?? "—"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="cb-modal-ft">
              <button className="cb-btn cb-btn--ghost" onClick={() => setSearchPreviewRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ SEARCH: DELETE CONFIRM MODAL ════════ */}
      {searchDeleteRow && (
        <div className="cb-overlay show">
          <div className="cb-modal" style={{ maxWidth: 420 }}>
            <div className="cb-modal-hd">
              <h3>Delete Booking</h3>
              <button className="cb-modal-x" onClick={() => setSearchDeleteRow(null)}>✕</button>
            </div>
            <div className="cb-modal-body">
              <p style={{ fontSize: 13, color: "var(--slate)", lineHeight: 1.6 }}>
                Are you sure you want to delete consignment{" "}
                <strong style={{ color: "var(--navy)" }}>
                  {searchDeleteRow.consignment_No ?? searchDeleteRow.Consignment_No}
                </strong>? This action cannot be undone.
              </p>
            </div>
            <div className="cb-modal-ft">
              <button className="cb-btn cb-btn--ghost" onClick={() => setSearchDeleteRow(null)} disabled={searchDeleting}>Cancel</button>
              <button className="cb-btn cb-btn--red" onClick={confirmSearchDelete} disabled={searchDeleting}>
                {searchDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ CONFIRM MODAL ════════ */}
      {confirmOpen && (
        <div className="cb-overlay show">
          <div className="cb-modal">
            <div className="cb-modal-hd">
              <h3>Confirm Booking</h3>
              <button className="cb-modal-x" onClick={() => setConfirmOpen(false)}>✕</button>
            </div>
            <div className="cb-modal-body">
              <p style={{ fontSize: 12.5, color: "var(--slate)", lineHeight: 1.6 }}>
                Review details before finalising. CNR and barcode sticker will be generated on confirmation.
              </p>
              <div className="cb-m-details">
                {[
                  ["CNR Number",     cnr],
                  ["Route",          `${bookingPlaceOpts.find(o => String(o.value) === String(bookingPlace))?.label || PLACE_NAMES[bookingPlace] || "—"} → ${deliveryPlaceOpts.find(o => String(o.value) === String(deliveryPlace))?.label || PLACE_NAMES[deliveryPlace] || "—"}`],
                  ["Party",          partyOpts.find(o => String(o.value) === String(partyKey))?.label || PARTIES[partyKey]?.name || "—"],
                  ["Pieces",         tQ + " pcs"],
                  ["Charged Weight", `Act: ${tA.toFixed(2)} kg | Chg: ${tC.toFixed(2)} kg`],
                  ["Images",         images.length ? images.length + " file(s)" : "None"],
                  ["Expected Del.",  formatDateDisplay(expDelivery)],
                  ["OTP Delivery",   otpOn ? `Enabled — ${otpMobile || "No mobile set"}` : "Not required"],
                  ["Notifications",  [smsOn && "SMS", emailOn && "Email", waOn && "WhatsApp"].filter(Boolean).join(" + ") || "None"],
                ].map(([lbl, val]) => (
                  <div key={lbl} className="cb-m-row">
                    <span className="ml">{lbl}</span>
                    <span className="mv" style={lbl === "CNR Number" ? { color: "var(--navy)", fontSize: 14, letterSpacing: 1 } : {}}>{val}</span>
                  </div>
                ))}
                <div className="cb-m-row" style={{ borderTop: "2px solid var(--border)", marginTop: 4, paddingTop: 8 }}>
                  <span className="ml" style={{ fontWeight: 700, color: "var(--teal)", fontSize: 13.5 }}>Total Payable</span>
                  <span className="mv" style={{ fontSize: 18, color: "var(--teal)" }}>₹ {fmt(rate.total)}</span>
                </div>
              </div>
            </div>
            <div className="cb-modal-ft">
              <button className="cb-btn cb-btn--ghost" onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button id="cb-confirm-modal-book-btn" className="cb-btn cb-btn--teal" onClick={doBook} disabled={isBooking}>
                {isBooking ? "Saving..." : "✓ Confirm & Book"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ SUCCESS MODAL ════════ */}
      {successOpen && (
        <div className="cb-overlay show">
          <div className="cb-modal" style={{ maxWidth: 440, textAlign: "center" }}>
            <div style={{ padding: "32px 28px 24px" }}>
              {/* Checkmark circle */}
              <div style={{
                width: 72, height: 72, borderRadius: "50%",
                background: "linear-gradient(135deg, #0D9488, #0F766E)",
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 20px", boxShadow: "0 8px 24px rgba(13,148,136,0.35)"
              }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.8"
                  strokeLinecap="round" strokeLinejoin="round"
                  style={{ width: 38, height: 38 }}>
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>

              <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800, color: "var(--navy, #1A2940)" }}>
                Booking Confirmed!
              </h2>
              <p style={{ margin: "0 0 22px", fontSize: 13, color: "var(--slate, #6B7A99)" }}>
                Your consignment has been booked successfully.
              </p>

              {/* CNR highlight box */}
              <div style={{
                background: "linear-gradient(135deg, #EFF9F8, #D0F2EF)",
                border: "2px solid #0D9488",
                borderRadius: 12, padding: "16px 20px", marginBottom: 22
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2,
                  textTransform: "uppercase", color: "#0F766E", marginBottom: 6 }}>
                  Consignment Number
                </div>
                <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 3,
                  color: "var(--navy, #1A2940)", fontFamily: "monospace" }}>
                  {bookedCnr}
                </div>
              </div>

              {/* Quick summary */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr",
                gap: 10, marginBottom: 24, textAlign: "left"
              }}>
                {[
                  ["From", stickerData.from],
                  ["To",   stickerData.to],
                  ["Consignor", stickerData.cnor],
                  ["Consignee", stickerData.cnee],
                  ["Pieces",    stickerData.pcs],
                  ["Amount",    stickerData.amt],
                ].map(([lbl, val]) => (
                  <div key={lbl} style={{
                    background: "#F7F9FC", borderRadius: 8, padding: "9px 12px"
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: 0.8, color: "#8AA5C0", marginBottom: 3 }}>{lbl}</div>
                    <div style={{ fontSize: 12.5, fontWeight: 700,
                      color: "var(--navy, #1A2940)", wordBreak: "break-word" }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", gap: 10 }}>
                <button id="cb-success-newbooking-btn" className="cb-btn cb-btn--ghost" style={{ flex: 1 }}
                  onClick={() => { setSuccessOpen(false); newBooking(); }}>
                  + New Booking
                </button>
                <button className="cb-btn cb-btn--teal" style={{ flex: 1 }}
                  onClick={() => { setSuccessOpen(false); setStickerOpen(true); }}>
                  🖨 View Sticker
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════ STICKER OVERLAY — now shows the real pcs-wise barcode label page ════════ */}
      {stickerOpen && (
        <div className="cb-sticker-overlay show">
          <div className="cb-sticker-card" style={{ width: "min(1280px, 96vw)", maxHeight: "94vh", overflow: "auto" }}>
            <Barcodelabel
              autoCnr={bookedCnr}
              onClose={() => setStickerOpen(false)}
              compact
              onNewBooking={() => { setStickerOpen(false); newBooking(); }}
            />
          </div>
        </div>
      )}

      {/* ── TOASTS ── */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MOBILE FLAT FORM
   Matches the plain single-page mobile mockup — no step
   wizard, no cards/bands. Every value here is wired to the
   exact same state as the desktop form (via props), so
   whatever the user fills in here is what gets booked —
   nothing is a separate copy of the data.
══════════════════════════════════════════════════ */
function MobileFlatForm({
  cnr, handleCnrChange, checkCnrDuplicate,
  cnorData, setCnorData,
  cneeData, setCneeData,
  bookingDate, setBookingDate, bookingTime,
  manualPcs, setManualPcs,
  manualChgWt, setManualChgWt,
  bookingPlace, onBookingPlaceChange, placeOpts,
  deliveryPlace, onDeliveryPlaceChange, delivPlaceOpts,
  partyKey, handlePartyChange, partySelectOpts,
  selectedJob, handleJobChange, jobOpts,
  onSubmit, isBooking,
}) {
  return (
    <div className="cb-card">
      <div className="cb-card-body">
        <div className="cb-field" style={{ marginBottom: 14 }}>
          <label>Consignment No.</label>
          <input
            value={cnr}
            onChange={e => handleCnrChange(e.target.value)}
            onBlur={e => checkCnrDuplicate(e.target.value)}
            placeholder="Consignment No."
            inputMode="numeric"
          />
        </div>

        <div className="cb-g2" style={{ marginBottom: 14 }}>
          <div className="cb-field">
            <label>Consignor Name</label>
            <input value={cnorData.name} onChange={e => setCnorData(d => ({ ...d, name: e.target.value }))} placeholder="Company / person name" />
          </div>
          <div className="cb-field">
            <label>Consignee Name</label>
            <input value={cneeData.name} onChange={e => setCneeData(d => ({ ...d, name: e.target.value }))} placeholder="Company / person name" />
          </div>
        </div>

        <div className="cb-g2" style={{ marginBottom: 14 }}>
          <div className="cb-field">
            <label>Consignor Mobile</label>
            <input
              value={cnorData.mobile}
              onChange={e => setCnorData(d => ({ ...d, mobile: e.target.value.replace(/\D/g, "") }))}
              placeholder="10-digit"
              maxLength={10}
              inputMode="numeric"
            />
          </div>
          <div className="cb-field">
            <label>Consignee Mobile</label>
            <input
              value={cneeData.mobile}
              onChange={e => setCneeData(d => ({ ...d, mobile: e.target.value.replace(/\D/g, "") }))}
              placeholder="10-digit"
              maxLength={10}
              inputMode="numeric"
            />
          </div>
        </div>

        <div className="cb-g2" style={{ marginBottom: 14 }}>
          <div className="cb-field">
            <label>Date</label>
            <input type="date" value={bookingDate} onChange={e => setBookingDate(e.target.value)} />
          </div>
          <div className="cb-field">
            <label>Time</label>
            <input className="ro" value={bookingTime} readOnly />
          </div>
        </div>

        <div className="cb-g2" style={{ marginBottom: 14 }}>
          <div className="cb-field">
            <label className="req">No. of Pieces</label>
            <input type="number" value={manualPcs} onChange={e => setManualPcs(e.target.value)} placeholder="0" />
          </div>
          <div className="cb-field">
            <label className="req">Weight (kg)</label>
            <input type="number" value={manualChgWt} onChange={e => setManualChgWt(e.target.value)} placeholder="0" />
          </div>
        </div>

        <div className="cb-g2" style={{ marginBottom: 14 }}>
          <div className="cb-field">
            <label className="req">Booking Place</label>
            <AutoComplete options={placeOpts} value={bookingPlace} onChange={onBookingPlaceChange} placeholder="Select branch…" />
          </div>
          <div className="cb-field">
            <label className="req">Delivery Place</label>
            <AutoComplete options={delivPlaceOpts} value={deliveryPlace} onChange={onDeliveryPlaceChange} placeholder="Select destination…" />
          </div>
        </div>

        <div className="cb-g2" style={{ marginBottom: 18 }}>
          <div className="cb-field">
            <label className="req">Party</label>
            <AutoComplete options={partySelectOpts} value={partyKey} onChange={handlePartyChange} placeholder="Search party…" />
          </div>
          <div className="cb-field">
            <label className="req">Job Name</label>
            <AutoComplete
              options={[{ value: "", label: partyKey ? "" : "Select a party first…" }, ...jobOpts]}
              value={selectedJob ?? ""}
              onChange={handleJobChange}
              placeholder={partyKey ? "Search job / department…" : "Select a party first…"}
            />
          </div>
        </div>

        <div className="cb-actions">
          <button className="cb-btn cb-btn--amber cb-btn--lg" style={{ width: "100%", justifyContent: "center" }} onClick={onSubmit} disabled={isBooking}>
            {isBooking ? "Saving..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}